import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import SimilarProporModal from '../components/cancelamentos/SimilarProporModal';
import { FotoLink } from '../components/similares/ComparativoSimilares';
import ComparativoCliente from '../components/similares/ComparativoCliente';
import { assinaturaDoUsuario } from '../components/atendimento/textReplacer';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertTriangle, CheckCircle, RefreshCw, Copy, ShoppingCart, Factory, FileWarning,
  Plus, Search, X, Save, ChevronDown, ChevronRight, BarChart3, Clock,
} from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status em que a entrega tem que ficar enquanto o caso está aberto (Adnéia,
// 08/10/2026): Erro na Nota em SEPARADO e ETR em RECEBIMENTO DE INSUCESSO. Fora
// dele, a Camila volta o pedido no Bseller. Caso real: 127626523, Erro na Nota
// que foi de Separado para Nota Fiscal Emitida no mesmo segundo.
const STATUS_ESPERADO = {
  erro_nota: { status: 'SEPARADO', rotulo: 'SEPARADO' },
  etr: { status: 'RECEBIMENTO DE INSUCESSO DE ENTREGA', rotulo: 'RECEBIMENTO DE INSUCESSO' },
};
const normStatus = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();
function alertaStatusCamila(tipo, item) {
  const regra = STATUS_ESPERADO[tipo];
  if (!regra || item.status !== 'pendente') return null;
  const atual = item.status_pedido_atual;
  if (!atual) return null;                       // fora do tabelão: não dá para afirmar
  // Erro na Nota com nova entrega lançada: a original é cancelada de propósito
  if (tipo === 'erro_nota' && item.nova_entrega) return null;
  if (normStatus(atual) === regra.status) return null;
  return { rotulo: regra.rotulo, atual };
}

// =================== HELPERS ===================
function copiar(texto, msg = 'Copiado!') {
  const str = String(texto || '');
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(str)
      .then(() => toast.success(msg))
      .catch(() => toast.error('Erro ao copiar'));
    return;
  }
  const el = document.createElement('textarea');
  el.value = str;
  el.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;z-index:9999';
  document.body.appendChild(el);
  el.focus(); el.select(); el.setSelectionRange(0, str.length);
  try {
    if (document.execCommand('copy')) toast.success(msg);
    else toast.error('Erro ao copiar');
  } catch { toast.error('Erro ao copiar'); }
  document.body.removeChild(el);
}

// Abre o produto numa nova aba ao clicar no nome: a página dele na loja VTEX,
// que tem todos os dados (Adnéia, 13/10/2026); sem ela, a imagem, como antes.
// A loja pede login da VTEX: serve à equipe, não vai no texto ao cliente.
async function abrirProdutoNaLoja(sku, getAuthHeader) {
  if (!sku) { toast.info('Produto sem SKU'); return; }
  const win = window.open('', '_blank');  // abre já (evita bloqueio de popup)
  try {
    const r = await axios.get(`${API_URL}/api/produtos/imagem/${encodeURIComponent(sku)}`, { headers: getAuthHeader() });
    const destino = r.data?.vtex_loja_url || r.data?.image_url;
    if (destino) {
      if (win) win.location.href = destino;
    } else {
      if (win) win.close();
      toast.info(`Produto ${sku} não encontrado na loja VTEX`);
    }
  } catch {
    if (win) win.close();
    toast.error('Erro ao buscar o produto na VTEX');
  }
}

function formatMoney(v) {
  const n = Number(String(v || '0').replace(',', '.'));
  if (isNaN(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatCPF(cpf) {
  if (!cpf) return '—';
  return String(cpf).replace(/\D/g, '').padStart(11, '0');
}

// Telefone do cliente: 11 digitos (DDD+9+8) ou 10 (DDD+8). Alguns vem com o
// 55 na frente — tira, senao o link do zap sai com 55 duplicado.
function foneDigitos(v) {
  let s = String(v || '').replace(/\D/g, '');
  if (s.length > 11 && s.startsWith('55')) s = s.slice(2);
  return s;
}

function foneBonito(v) {
  const s = foneDigitos(v);
  if (s.length === 11) return `(${s.slice(0, 2)}) ${s.slice(2, 7)}-${s.slice(7)}`;
  if (s.length === 10) return `(${s.slice(0, 2)}) ${s.slice(2, 6)}-${s.slice(6)}`;
  return s;
}

// Calcula dias pendentes a partir da data importada (YYYY-MM-DD)
function diasPendentes(item) {
  if (item.status === 'encerrado') return null;
  const dataStr = item.data || '';
  // data formato YYYY-MM-DD
  const m = String(dataStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return item.dias_em_status || 0;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const hoje = new Date();
  const diff = Math.floor((hoje - d) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

function StatusBadge({ item }) {
  if (item.status === 'encerrado') {
    return <span className="text-emerald-600 text-xs font-semibold">✓</span>;
  }
  const dias = diasPendentes(item);
  if (dias >= 7) {
    return <span className="text-red-600 text-xs font-semibold whitespace-nowrap">{dias}d ⚠</span>;
  }
  return <span className="text-amber-600 text-xs font-semibold whitespace-nowrap">{dias || 0}d</span>;
}

// =================== MODAL: NOVO CANCELAMENTO ===================
function NovoCancelamentoDialog({ open, onClose, tipo, onCreated }) {
  const { getAuthHeader } = useAuth();
  const [numeroPedido, setNumeroPedido] = useState('');
  const [dadosPedido, setDadosPedido] = useState(null);
  const [itensEntrega, setItensEntrega] = useState([]);   // todos os itens da entrega
  const [itemSelIdx, setItemSelIdx] = useState(0);          // índice do item escolhido
  const [alerta, setAlerta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);

  // Campos do form
  const [motivo, setMotivo] = useState('');
  const [acao, setAcao] = useState('Cancelar');
  const [motivoRejeicao, setMotivoRejeicao] = useState('');
  const [ticket, setTicket] = useState('');
  const [instancia, setInstancia] = useState('');
  const [zeradoReserva, setZeradoReserva] = useState(null);
  const [observacao, setObservacao] = useState('');

  const limpar = () => {
    setNumeroPedido(''); setDadosPedido(null); setItensEntrega([]); setItemSelIdx(0); setAlerta(null);
    setMotivo(''); setAcao('Cancelar'); setMotivoRejeicao('');
    setTicket(''); setInstancia(''); setZeradoReserva(null); setObservacao('');
  };

  // Item efetivamente selecionado: se a entrega tem >1 item, usa o escolhido;
  // senão usa o item único (do tabelão).
  const itemAtual = (itensEntrega && itensEntrega.length > 0)
    ? itensEntrega[itemSelIdx] || itensEntrega[0]
    : (dadosPedido ? {
        produto: dadosPedido.produto,
        codigo_item_bseller: dadosPedido.codigo_item_bseller,
        codigo_item_vtex: dadosPedido.codigo_item_vtex,
        codigo_fornecedor: dadosPedido.codigo_fornecedor,
        preco_final: dadosPedido.preco_final,
        quantidade: dadosPedido.quantidade,
      } : null);

  useEffect(() => { if (open) limpar(); }, [open]);

  const buscarPedido = async () => {
    if (!numeroPedido.trim()) return;
    setLoading(true);
    setDadosPedido(null);
    setAlerta(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/cancelamentos/lookup/${numeroPedido.trim()}`,
        { headers: getAuthHeader() }
      );
      if (res.data?.encontrado) {
        setDadosPedido(res.data.dados);
        const itens = Array.isArray(res.data.itens) ? res.data.itens : [];
        setItensEntrega(itens);
        // Default: item que casa com o do tabelão; senão o primeiro
        const bsellerTab = res.data.dados?.codigo_item_bseller;
        const idx = itens.findIndex(it => it.codigo_item_bseller === bsellerTab);
        setItemSelIdx(idx >= 0 ? idx : 0);
        setAlerta(res.data.alerta_produto || null);
      } else {
        toast.error('Pedido não encontrado no tabelão');
      }
    } catch (e) {
      toast.error('Erro ao buscar pedido');
    } finally { setLoading(false); }
  };

  const salvar = async () => {
    if (!dadosPedido) { toast.error('Busque o pedido primeiro'); return; }
    setSalvando(true);
    try {
      const payload = {
        tipo,
        numero_pedido: numeroPedido.trim(),
        motivo: motivo || undefined,
        acao,
        motivo_rejeicao: motivoRejeicao || undefined,
        ticket: ticket || undefined,
        instancia: instancia || undefined,
        zerado_reserva: zeradoReserva,
        observacao: observacao || undefined,
      };
      // Item escolhido (entrega com mais de 1 item) — sobrescreve o item do tabelão
      // e marca como PARCIAL (cancelamento/similar de 1 item de vários).
      if (itensEntrega.length > 1 && itemAtual) {
        payload.item_produto = itemAtual.produto || undefined;
        payload.item_codigo_bseller = itemAtual.codigo_item_bseller || undefined;
        payload.item_codigo_vtex = itemAtual.codigo_item_vtex || undefined;
        payload.item_codigo_fornecedor = itemAtual.codigo_fornecedor || undefined;
        payload.item_preco_final = itemAtual.preco_final != null ? String(itemAtual.preco_final) : undefined;
        payload.item_quantidade = itemAtual.quantidade != null ? String(itemAtual.quantidade) : undefined;
        payload.is_parcial = true;
      }
      await axios.post(`${API_URL}/api/cancelamentos`, payload, { headers: getAuthHeader() });
      toast.success('Cancelamento registrado!');
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao registrar');
    } finally { setSalvando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {tipo === 'aes' && <><ShoppingCart className="h-5 w-5 text-orange-600" /> Novo Cancelamento AES (Compras)</>}
            {tipo === 'etr' && <><Factory className="h-5 w-5 text-purple-600" /> Novo Cancelamento ETR (Produção)</>}
            {tipo === 'erro_nota' && <><FileWarning className="h-5 w-5 text-red-600" /> Novo Erro na Nota</>}
          </DialogTitle>
          <DialogDescription>
            Digite o número da Entrega — o sistema busca os dados do pedido automaticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Buscar */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Número da Entrega *</label>
            <div className="flex gap-2">
              <Input
                placeholder="Ex.: 122415418"
                value={numeroPedido}
                onChange={e => setNumeroPedido(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); buscarPedido(); } }}
              />
              <Button onClick={buscarPedido} disabled={loading} variant="secondary">
                {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-1" /> Buscar</>}
              </Button>
            </div>
          </div>

          {/* Alerta de produto recorrente */}
          {alerta && (
            <div className="p-3 rounded-lg bg-red-50 border-2 border-red-300 dark:bg-red-950/30 dark:border-red-800">
              <p className="font-bold text-red-800 dark:text-red-200 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" /> ATENÇÃO — Produto recorrente
              </p>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">{alerta.mensagem}</p>
            </div>
          )}

          {/* Seletor de item — só quando a entrega tem mais de 1 item */}
          {dadosPedido && itensEntrega.length > 1 && (
            <div className="p-3 rounded-lg bg-amber-50 border-2 border-amber-300 dark:bg-amber-950/30 dark:border-amber-800">
              <label className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-1 flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" /> Esta entrega tem {itensEntrega.length} itens — selecione o item do cancelamento
              </label>
              <select
                value={itemSelIdx}
                onChange={e => setItemSelIdx(Number(e.target.value))}
                className="w-full px-3 py-2 border rounded-md text-sm bg-background"
              >
                {itensEntrega.map((it, i) => (
                  <option key={it.codigo_item_bseller || i} value={i}>
                    {it.produto} — SKU {it.codigo_item_vtex || it.codigo_item_bseller} ({formatMoney(it.preco_final)})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Dados do pedido (preview) */}
          {dadosPedido && (
            <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900/30 border space-y-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Cliente:</span> <strong>{dadosPedido.nome_cliente || '—'}</strong></div>
                <div><span className="text-slate-500">CPF:</span> {formatCPF(dadosPedido.cpf_cliente)}</div>
                <div><span className="text-slate-500">Canal:</span> {dadosPedido.canal_vendas || '—'}</div>
                <div><span className="text-slate-500">Filial:</span> {dadosPedido.filial || '—'}</div>
                <div className="col-span-2"><span className="text-slate-500">Produto:</span> <strong>{itemAtual?.produto || dadosPedido.produto || '—'}</strong></div>
                <div><span className="text-slate-500">SKU:</span> {itemAtual?.codigo_item_vtex || itemAtual?.codigo_item_bseller || '—'}</div>
                <div><span className="text-slate-500">Fornecedor:</span> {itemAtual?.codigo_fornecedor || '—'}</div>
                <div><span className="text-slate-500">Status atual:</span> <Badge variant="outline" className="text-xs">{dadosPedido.status_pedido || '—'}</Badge></div>
                <div><span className="text-slate-500">Valor da venda:</span> <strong className="text-emerald-700">{formatMoney(itemAtual?.preco_final)}</strong></div>
                {dadosPedido.nota_fiscal && <div><span className="text-slate-500">NF:</span> {String(dadosPedido.nota_fiscal).split('.')[0]}</div>}
              </div>
            </div>
          )}

          {/* Campos específicos por tipo */}
          {dadosPedido && (
            <div className="space-y-3">
              {tipo === 'etr' && (
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Motivo *</label>
                  <select
                    value={motivo}
                    onChange={e => setMotivo(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md text-sm bg-background"
                  >
                    <option value="">Selecione...</option>
                    <option value="Falta">Falta no estoque</option>
                    <option value="Perda/Quebra">Perda/Quebra</option>
                    <option value="Falha de cadastro">Falha de cadastro</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>
              )}

              {tipo === 'erro_nota' && (
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Motivo da Rejeição *</label>
                  <select
                    value={motivoRejeicao}
                    onChange={e => setMotivoRejeicao(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md text-sm bg-background"
                  >
                    <option value="">Selecione...</option>
                    <option value="Irregularidade fiscal do destinatário">Irregularidade fiscal do destinatário</option>
                    <option value="CPF/CNPJ inválido">CPF/CNPJ inválido</option>
                    <option value="Endereço inválido">Endereço inválido</option>
                    <option value="CEP inexistente">CEP inexistente</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>
              )}

              {tipo !== 'erro_nota' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Ação</label>
                    <select
                      value={acao}
                      onChange={e => setAcao(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md text-sm bg-background"
                    >
                      <option value="Cancelar">Cancelar</option>
                      <option value="Similar">Similar (oferecer produto substituto)</option>
                      <option value="Reenviar">Reenviar</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Ticket</label>
                    <Input value={ticket} onChange={e => setTicket(e.target.value)} placeholder="Nº do ticket no parceiro" />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Instância (Bseller)</label>
                  <Input value={instancia} onChange={e => setInstancia(e.target.value)} placeholder="Nº instância" />
                </div>
                {tipo === 'aes' && (
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Zerado reserva?</label>
                    <select
                      value={zeradoReserva === null ? '' : zeradoReserva ? 'sim' : 'nao'}
                      onChange={e => {
                        const v = e.target.value;
                        setZeradoReserva(v === '' ? null : v === 'sim');
                      }}
                      className="w-full px-3 py-2 border rounded-md text-sm bg-background"
                    >
                      <option value="">—</option>
                      <option value="sim">Sim</option>
                      <option value="nao">Não</option>
                    </select>
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Observação</label>
                <Textarea
                  value={observacao}
                  onChange={e => setObservacao(e.target.value)}
                  placeholder="Anotações sobre este cancelamento..."
                  rows={3}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={salvar} disabled={!dadosPedido || salvando}>
            {salvando ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Salvando...</> : <><Save className="h-4 w-4 mr-2" /> Registrar</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Trava dos 10 dias do AES (Adnéia, 21/10/2026): a mesma conta do servidor
// (routes/cancelamentos.py, aes_cancelar_a_partir_de). Dias CORRIDOS desde que entrou na
// tela; antes disso o ticket de cancelamento não grava. null = livre.
export const aesCancelarAPartirDe = (item) => {
  if ((item?.tipo || 'aes') !== 'aes' || item?.liberado_antes_em) return null;
  const m = String(item?.data || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const libera = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + 10);
  const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
  return hoje < libera ? libera : null;
};

// =================== LINHA EDITÁVEL (auto-save no blur) ===================
// Hook que envolve um input/textarea: salva no blur se o valor mudou
function useFieldSave(item, field, onSaved) {
  const { getAuthHeader } = useAuth();
  const [val, setVal] = React.useState(item[field] || '');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => { setVal(item[field] || ''); }, [item[field]]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = React.useCallback(async () => {
    const original = item[field] || '';
    if (val === original) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/cancelamentos/${item.id}`,
        { [field]: val },
        { headers: getAuthHeader() }
      );
      onSaved?.();
    } catch (e) {
      // A trava dos 10 dias do AES volta com o motivo e a data: mostrar, não só "Erro".
      toast.error(e?.response?.data?.detail || 'Erro ao salvar');
      setVal(original); // rollback
    } finally { setSaving(false); }
  }, [val, item, field, getAuthHeader, onSaved]);

  return { val, setVal, save, saving };
}

// Input de texto editável que salva automaticamente
function CellInput({ item, field, placeholder, onSaved, className = '' }) {
  const { val, setVal, save, saving } = useFieldSave(item, field, onSaved);
  return (
    <div className="relative">
      <input
        type="text"
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.target.blur();  // dispara o save
          }
        }}
        placeholder={placeholder}
        className={`w-full px-2 py-1.5 text-sm border rounded bg-background focus:ring-1 focus:ring-blue-300 focus:border-blue-400 ${className}`}
      />
      {saving && <RefreshCw className="absolute right-1 top-1/2 -translate-y-1/2 h-3 w-3 animate-spin text-slate-400" />}
    </div>
  );
}

// Textarea editável que salva automaticamente
function CellTextarea({ item, field, placeholder, onSaved }) {
  const { val, setVal, save, saving } = useFieldSave(item, field, onSaved);
  return (
    <div className="relative">
      <textarea
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={save}
        placeholder={placeholder}
        rows={2}
        className="w-full px-2 py-1.5 text-sm border rounded bg-background focus:ring-1 focus:ring-blue-300 focus:border-blue-400 resize-y min-h-[2.5rem]"
      />
      {saving && <RefreshCw className="absolute right-1 top-1 h-3 w-3 animate-spin text-slate-400" />}
    </div>
  );
}


// Campo de lookup de SKU para item similar — digita o SKU, busca nome+ID automaticamente
function SimilarLookupCell({ item, onSaved }) {
  const { getAuthHeader } = useAuth();
  const [sku, setSku] = React.useState(item.sku_similar || '');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => { setSku(item.sku_similar || ''); }, [item.sku_similar]);

  // Auto-lookup: se tem SKU mas não tem nome, busca automaticamente ao montar
  React.useEffect(() => {
    if (item.sku_similar && !item.nome_similar) {
      const skuClean = item.sku_similar.trim().toUpperCase();
      setLoading(true);
      setError('');
      axios.get(`${API_URL}/api/cancelamentos/lookup-sku/${encodeURIComponent(skuClean)}`, { headers: getAuthHeader() })
        .then(r => axios.put(`${API_URL}/api/cancelamentos/${item.id}`,
          { sku_similar: skuClean, nome_similar: r.data.nome, id_similar: r.data.id_bseller },
          { headers: getAuthHeader() }
        ).then(() => onSaved?.()))
        .catch(err => {
          const msg = err.response?.data?.detail || 'SKU não encontrado no servidor';
          setError(msg);
        })
        .finally(() => setLoading(false));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLookup = React.useCallback(async () => {
    const skuClean = sku.trim().toUpperCase();
    const original = (item.sku_similar || '').trim().toUpperCase();
    if (!skuClean || skuClean === original) return;

    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API_URL}/api/cancelamentos/lookup-sku/${encodeURIComponent(skuClean)}`, { headers: getAuthHeader() });
      // Found — save sku_similar, nome_similar, id_similar
      await axios.put(`${API_URL}/api/cancelamentos/${item.id}`,
        { sku_similar: skuClean, nome_similar: r.data.nome, id_similar: r.data.id_bseller },
        { headers: getAuthHeader() }
      );
      onSaved?.();
    } catch (err) {
      const msg = err.response?.data?.detail || 'SKU não encontrado no servidor';
      setError(msg);
      setSku(original);  // rollback
    } finally { setLoading(false); }
  }, [sku, item, getAuthHeader, onSaved]);

  return (
    <div className="flex flex-col gap-1">
      <div className="relative">
        <input
          type="text"
          value={sku}
          onChange={e => { setSku(e.target.value.toUpperCase()); setError(''); }}
          onBlur={handleLookup}
          placeholder="SKU do similar..."
          className={`w-full px-2 py-1.5 text-xs border rounded bg-background focus:ring-1 focus:ring-blue-300 focus:border-blue-400 font-mono ${error ? 'border-red-400 bg-red-50' : item.nome_similar ? 'border-green-400' : ''}`}
        />
        {loading && <RefreshCw className="absolute right-1 top-1/2 -translate-y-1/2 h-3 w-3 animate-spin text-slate-400" />}
      </div>
      {error && <p className="text-xs text-red-600 leading-tight">{error}</p>}
    </div>
  );
}

// Célula de PROPOSTA de similares — mostra SKUs sugeridos para o analista escolher.
// Analista marca 1+ e clica "Propor" (vira Similar) ou "Seguir com cancelamento".
function SimilarPropostaCell({ item, onSaved }) {
  const { getAuthHeader } = useAuth();
  const sugeridos = item.similares_sugeridos || [];
  const [sel, setSel] = React.useState(() => new Set());
  const [skuManual, setSkuManual] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const toggle = (sku) => {
    setSel(prev => {
      const n = new Set(prev);
      n.has(sku) ? n.delete(sku) : n.add(sku);
      return n;
    });
  };

  const totalSel = sel.size + (skuManual.trim() ? 1 : 0);

  const propor = async () => {
    const skus = [...Array.from(sel)];
    if (skuManual.trim()) skus.push(skuManual.trim().toUpperCase());
    if (skus.length === 0) { toast.error('Selecione um similar ou digite o SKU'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/cancelamentos/${item.id}/propor-similares`,
        { skus }, { headers: getAuthHeader() });
      toast.success('Similar(es) proposto(s)');
      onSaved?.();
    } catch (e) {
      toast.error('Erro: ' + (e?.response?.data?.detail || e?.message));
    } finally { setSaving(false); }
  };

  const seguirCancelamento = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/cancelamentos/${item.id}/seguir-cancelamento`,
        {}, { headers: getAuthHeader() });
      toast.success('Seguindo com cancelamento');
      onSaved?.();
    } catch (e) {
      toast.error('Erro: ' + (e?.response?.data?.detail || e?.message));
    } finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col gap-1 min-w-[200px]">
      <div className="text-[11px] font-semibold text-purple-700 flex items-center gap-1">
        🔎 {sugeridos.length > 0 ? 'Similares sugeridos:' : 'Revisar similar'}
      </div>
      {sugeridos.length === 0 && (
        <div className="text-[10px] text-slate-500 leading-tight">
          Sem similar automático — digite o SKU de um similar ou siga para o cancelamento.
        </div>
      )}
      <div className="flex flex-col gap-0.5 max-h-[120px] overflow-y-auto">
        {sugeridos.map((s, i) => (
          <label key={s.sku} className="flex items-start gap-1 text-[11px] cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
            <input type="checkbox" checked={sel.has(s.sku)} onChange={() => toggle(s.sku)} className="mt-0.5" />
            <span className="leading-tight">
              <span
                className="block text-slate-700 font-medium hover:text-blue-600 hover:underline cursor-pointer"
                title="Clique para abrir o produto na loja (VTEX)"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); abrirProdutoNaLoja(s.sku, getAuthHeader); }}
              >{s.nome}</span>
              <span className="block text-[10px] text-slate-500">
                <span
                  className="font-mono hover:text-blue-600 hover:underline cursor-pointer"
                  title="Clique para copiar o SKU"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); copiar(s.sku, `SKU ${s.sku} copiado!`); }}
                >{s.sku}</span>
                {s.xd > 0 && <span className="text-emerald-600"> · XD {s.xd}</span>}
                {s.ufs?.length > 0 && <span className="text-slate-400"> ({s.ufs.join('/')})</span>}
              </span>
              {s.alerta_filial && <span className="block text-red-600 text-[10px]">⚠️ {s.alerta_filial}</span>}
            </span>
          </label>
        ))}
      </div>
      <input
        type="text"
        value={skuManual}
        onChange={(e) => setSkuManual(e.target.value.toUpperCase())}
        placeholder="ou digite o SKU do similar"
        className="text-[11px] px-1.5 py-1 rounded border border-slate-300 font-mono focus:outline-none focus:border-purple-400"
      />
      <div className="flex gap-1 mt-0.5">
        <button onClick={propor} disabled={saving}
          className="flex-1 text-[11px] px-2 py-1 rounded bg-green-100 text-green-700 border border-green-300 hover:bg-green-200 font-semibold disabled:opacity-50">
          Propor ({totalSel})
        </button>
        <button onClick={seguirCancelamento} disabled={saving}
          className="flex-1 text-[11px] px-2 py-1 rounded bg-slate-100 text-slate-600 border border-slate-300 hover:bg-slate-200 disabled:opacity-50"
          title="Não há similar adequado — seguir com o cancelamento normal">
          Cancelar
        </button>
      </div>
    </div>
  );
}

// Checkbox de "encerrado" que registra/limpa a data automaticamente
// Pendencia (Adneia, 15/09): o seletor ja vem com a sugestao do sistema; ao escolher,
// a escolha e gravada, vira a verdade daquele registro e a data entra na observacao.
const PENDENCIAS = ['Ag. tratativa', 'Ag. Cliente', 'Ag. Parceiro', 'Ag. Bseller', 'Encerrado'];
// Erro na Nota tem um estágio a mais: com o pedido novo já subido no CPF, o
// caso fica em acompanhamento (Ag. compras / Ag. logística) até ser entregue —
// e só encerra quando a entrega é dada na VTEX.
const PENDENCIAS_ERRO_NOTA = ['Ag. tratativa', 'Ag. Cliente', 'Ag. Parceiro', 'Ag. Bseller',
  'Ag. Entrega', 'Encerrado'];
const pendenciasDe = (tipo) => (tipo === 'erro_nota' ? PENDENCIAS_ERRO_NOTA : PENDENCIAS);

function PendenciaCell({ item, tipo, onSaved, onPatchLocal }) {
  const { getAuthHeader } = useAuth();
  const [saving, setSaving] = React.useState(false);
  const atual = item.pendencia || item.pendencia_auto || 'Ag. tratativa';
  const manual = !!item.pendencia;

  const escolher = async (valor) => {
    if (!valor || valor === item.pendencia) return;
    setSaving(true);
    try {
      // A observacao NAO vai no payload: quem anota e o backend, lendo o texto
      // atual do registro. Mandar daqui sobrescrevia o que a pessoa tinha
      // acabado de digitar (pedido da Adneia, 17/09).
      // "Encerrado" na pendencia FINALIZA o registro (a coluna Encerrado saiu da tela em
      // 15/09). Escolher outra coisa num registro encerrado REABRE, como o antigo checkbox.
      const payload = { pendencia: valor };
      if (valor === 'Encerrado') {
        payload.status = 'encerrado';
      } else if (item.status === 'encerrado') {
        payload.status = 'pendente';
        payload.data_encerramento = '';
      }
      const resp = await axios.put(`${API_URL}/api/cancelamentos/${item.id}`, payload, { headers: getAuthHeader() });
      // Atualiza so esta linha na tela — a lista nao recarrega sozinha. A
      // observacao vem do que o servidor gravou, nao do que a tela achava.
      const gravado = resp?.data?.cancelamento || {};
      onPatchLocal?.(item.id, { ...payload, observacao: gravado.observacao ?? item.observacao });
      toast.success(`Pendência: ${valor}`);
      onSaved?.();
    } catch (e) {
      toast.error('Erro ao salvar pendência: ' + (e?.response?.data?.detail || e?.message || ''));
    } finally { setSaving(false); }
  };

  return (
    <select
      value={atual}
      onChange={(e) => escolher(e.target.value)}
      disabled={saving}
      title={manual ? 'Definida manualmente' : 'Sugerida pelo sistema — escolha para fixar'}
      className={`w-full text-xs border rounded px-1.5 py-1 bg-white dark:bg-slate-900 ${manual
        ? 'border-blue-300 dark:border-blue-800 text-blue-800 dark:text-blue-300 font-medium'
        : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 italic'}`}
    >
      {pendenciasDe(tipo).map(p => <option key={p} value={p}>{p}</option>)}
    </select>
  );
}

function CellEncerrado({ item, onSaved }) {
  const { getAuthHeader } = useAuth();
  const [saving, setSaving] = React.useState(false);
  const encerrado = item.status === 'encerrado';

  const toggle = async () => {
    setSaving(true);
    try {
      const payload = encerrado
        ? { status: 'pendente', data_encerramento: '' }
        : { status: 'encerrado' };
      await axios.put(`${API_URL}/api/cancelamentos/${item.id}`, payload, { headers: getAuthHeader() });
      onSaved?.();
    } catch {
      toast.error('Erro ao alternar status');
    } finally { setSaving(false); }
  };

  return (
    <label className="inline-flex items-center gap-1 cursor-pointer">
      <input
        type="checkbox"
        checked={encerrado}
        onChange={toggle}
        disabled={saving}
        className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
      />
      {encerrado && item.data_encerramento && (
        <span className="text-[10px] text-slate-500 whitespace-nowrap">{formatData(item.data_encerramento)}</span>
      )}
    </label>
  );
}

// Formata data ISO ou YYYY-MM-DD para DD/MM/YYYY
function formatData(d) {
  if (!d) return '—';
  const s = String(d);
  if (s.includes('T')) {
    const dt = new Date(s);
    if (!isNaN(dt)) return dt.toLocaleDateString('pt-BR');
  }
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
}

// =================== HELPER: CANAL → MÉTODO ===================
function getCanalMetodo(canalVendas) {
  const c = (canalVendas || '').toLowerCase().trim();
  // LL especial — checar antes de "ticket"
  if (c.includes('ll loyalty') || c === 'lll' || /\blll\b/.test(c)) return 'll';
  // Direto no portal
  if (c.includes('livelo')) return 'portal';
  if (c.includes('sicredi') || c === 'scd' || /\bscd\b/.test(c)) return 'portal';
  // ShopHub saiu daqui (Adnéia, 29/09): a regra procurava o código 'SHB',
  // que nunca aparece — o canal é gravado como 'ShopHub' nos 14 registros
  // que existem. Na prática sempre caiu no e-mail, e é por e-mail mesmo.
  // Tirar a linha não muda comportamento; só para de prometer um portal
  // que a tela nunca abriu.
  // Chamado
  if (c.includes('coopera')) return 'chamado';
  // Ocorrência
  if (c === 'csu' || /\bcsu\b/.test(c)) return 'ocorrencia';
  // Ticket genérico
  if (c.includes('tudo azul') || c === 'ta' || /\bta\b/.test(c)) return 'ticket';
  // E-mail: camicado, grs, global rewards, ltm, nice, nicequest, senff, e restantes
  return 'email';
}

const METODO_LABEL = {
  email:     'E-mail',
  chamado:   'Chamado',
  ocorrencia:'Ocorrência no Portal',
  ticket:    'Ticket no Portal',
  ll:        'Ticket no Portal (LL)',
  portal:    'Direto no Portal',
};

// =================== MODAL: TEXTO CANCELAMENTO ===================
function TextoModal({ item, tipo, onClose }) {
  const { user, getAuthHeader } = useAuth();
  const canalVendas = item.canal_vendas || item.parceiro_planilha || '';
  const metodo = getCanalMetodo(canalVendas);

  const produto    = item.produto       || '[produto]';
  const entrega    = item.numero_pedido || '[entrega]';
  const cpf        = item.cpf_cliente   ? formatCPF(item.cpf_cliente) : '[CPF]';
  const assinatura = assinaturaDoUsuario(user) || '[nome]';
  const pedidoLL   = item.pedido_externo || item.pedido_bseller || item.numero_pedido || '[número do pedido]';

  // ---- Resposta ao canal (Adneia, 15/09) ----
  // ATENCAO: estes hooks precisam ficar ANTES do return do metodo 'portal' logo abaixo,
  // senao viram hooks condicionais e a tela quebra.
  // O nome do contato e lembrado POR CANAL (cada canal tem a sua pessoa) e a saudacao
  // acompanha o horario. No cancelamento PARCIAL o texto cita os itens que seguem em
  // entrega — buscados no lookup da entrega, que ja devolve todos os itens do tabelao.
  const chaveContato = `elo_contato_canal_${(canalVendas || '').toLowerCase()}`;
  const [nomeContato, setNomeContato] = React.useState(() => {
    try { return localStorage.getItem(chaveContato) || ''; } catch { return ''; }
  });
  React.useEffect(() => {
    try { localStorage.setItem(chaveContato, nomeContato); } catch {}
  }, [chaveContato, nomeContato]);

  const [outrosItens, setOutrosItens] = React.useState([]);
  React.useEffect(() => {
    if (!item.is_parcial || !item.numero_pedido) return;
    let vivo = true;
    axios.get(`${API_URL}/api/cancelamentos/lookup/${item.numero_pedido}`, { headers: getAuthHeader() })
      .then(r => {
        if (!vivo) return;
        const itens = Array.isArray(r.data?.itens) ? r.data.itens : [];
        const meu = String(item.codigo_item_vtex || '').toUpperCase();
        setOutrosItens(itens
          .filter(it => String(it.codigo_item_vtex || '').toUpperCase() !== meu)
          .map(it => it.produto)
          .filter(Boolean));
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, [item.is_parcial, item.numero_pedido, item.codigo_item_vtex, getAuthHeader]);

  const saudacao = (() => {
    const h = new Date().getHours();
    return h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
  })();
  const abertura = `Olá${nomeContato ? `, ${nomeContato}` : ''}. ${saudacao}!`;
  const respostaCanal = (item.is_parcial && outrosItens.length)
    ? `${abertura}\nAgradecemos o retorno. Seguimos com o cancelamento parcial do item: ${produto}. Ressalto que ${outrosItens.length > 1 ? 'os itens' : 'o item'}: ${outrosItens.join(', ')} segue${outrosItens.length > 1 ? 'm' : ''} em processo de entrega ao cliente.`
    : `${abertura}\nAgradecemos o retorno. Seguimos com o cancelamento item: ${produto}.`;

  // ---- Direto no portal ----
  if (metodo === 'portal') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
        <div
          className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-sm mx-4 p-5 space-y-4"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-base flex items-center gap-2">
              🌐 Acionamento — {canalVendas}
            </h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="text-center py-4 space-y-3">
            <div className="text-5xl">🌐</div>
            <p className="font-semibold text-slate-700 dark:text-slate-200">Cancelamento direto no portal</p>
            <p className="text-sm text-slate-500">
              Acesse o portal do parceiro <strong>{canalVendas}</strong> e registre o cancelamento diretamente.
            </p>
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-3 space-y-2 text-left">
              {(() => {
                const pedidoSemPrefixo = (item.pedido_externo || '').replace(/^[A-Za-z]+-/, '');
                return (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-500">Pedido:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-sm text-slate-700 dark:text-slate-200">
                        {pedidoSemPrefixo || <span className="text-slate-400 italic">não informado</span>}
                      </span>
                      {pedidoSemPrefixo && (
                        <button
                          onClick={() => copiar(pedidoSemPrefixo, 'Pedido copiado!')}
                          className="text-slate-400 hover:text-blue-600"
                          title="Copiar pedido"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-slate-500">Entrega:</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-sm text-slate-700 dark:text-slate-200">{entrega}</span>
                  <button
                    onClick={() => copiar(entrega, 'Entrega copiada!')}
                    className="text-slate-400 hover:text-blue-600"
                    title="Copiar entrega"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-slate-500">CPF cliente:</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-sm text-slate-700 dark:text-slate-200">{cpf}</span>
                  <button
                    onClick={() => copiar(item.cpf_cliente || '', 'CPF copiado!')}
                    className="text-slate-400 hover:text-blue-600"
                    title="Copiar CPF"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- E-mail / chamado / ocorrência / ticket ----
  const isLL = metodo === 'll';

  // Pedido externo sem prefixo do parceiro (ex.: LLL-7c89 → 7c89, PTM-4773 → 4773)
  const pedidoExternoSemPrefixo = (item.pedido_externo || '').replace(/^[A-Za-z]+-/, '');
  // Identificador do assunto/corpo: a ENTREGA (Adneia, 14/09). Antes ia o pedido
  // externo do parceiro quando existia — e era esse número que aparecia no texto,
  // em vez da entrega que a operação usa pra se achar. Livelo segue com o ID dela
  // (pedidoLL), que é o que aquele canal reconhece.
  const pedidoParaAssunto = entrega || pedidoExternoSemPrefixo;

  // ASSUNTO: sempre o pedido do cliente no canal, nunca a entrega (Adnéia,
  // 09/10/2026: "no assunto nunca deve estar a entrega"; começou pela LTM e
  // pela Nice). Vai como está no tabelão (LTM-xxxx, SCD-..., LLL-...), que é o
  // número que o canal procura. O LL segue com pedidoLL (o mesmo pedido_externo
  // primeiro). O CORPO continua com a entrega (regra de 14/09).
  const pedidoAssunto = isLL ? pedidoLL
    : (item.pedido_externo || '[pedido do cliente não encontrado]');
  const assunto = `Cancelamento - Pedido: ${pedidoAssunto} - CPF: ${cpf}`;

  // ---- ETR: avaria, com versão total e parcial (Adneia, 16/09) ----
  // A LL mantém o formato dela (ID Pedido / CPF / Produto), só troca o motivo.
  const etrTotal = isLL
    ? `Olá,\n\nInfelizmente, durante a preparação do item abaixo, identificamos uma avaria, o que nos levou a optar pelo cancelamento devido à indisponibilidade para reposição.\n\nID Pedido: ${pedidoLL}\nCPF cliente: ${cpf}\nProduto(s): ${produto}\n\nPoderia, por gentileza, seguir com o cancelamento e o estorno ao cliente?\n\nAtenciosamente,\n${assinatura}`
    : `Olá,\n\nInfelizmente, durante a preparação do item ${produto} - ${pedidoParaAssunto}, identificamos uma avaria, o que nos levou a optar pelo cancelamento devido à indisponibilidade para reposição.\n\nPoderia, por gentileza, seguir com o cancelamento e o estorno ao cliente?\n\nAtenciosamente,\n${assinatura}`;

  const etrParcial = isLL
    ? `Olá,\n\nInfelizmente, durante a preparação do item abaixo, identificamos uma avaria, o que nos levou a optar pelo cancelamento parcial devido à indisponibilidade para reposição.\n\nID Pedido: ${pedidoLL}\nCPF cliente: ${cpf}\nProduto(s): ${produto}\n\nPoderia, por gentileza, seguir com o cancelamento parcial e o estorno ao cliente? Ressalto que já seguimos com o envio dos demais itens.\n\nAtenciosamente,\n${assinatura}`
    : `Olá,\n\nInfelizmente, durante a preparação do item: ${produto} - ${pedidoParaAssunto}, identificamos uma avaria, o que nos levou a optar pelo cancelamento parcial devido à indisponibilidade para reposição.\n\nPoderia, por gentileza, seguir com o cancelamento parcial e o estorno ao cliente? Ressalto que já seguimos com o envio dos demais itens.\n\nAtenciosamente,\n${assinatura}`;

  const corpo = isLL
    ? `Olá,\n\nInfelizmente, durante a preparação do item abaixo, identificamos falha no pedido, o que nos levou a optar pelo cancelamento.\n\nID Pedido: ${pedidoLL}\nCPF cliente: ${cpf}\nProduto(s): ${produto}\n\nPoderia, por gentileza, seguir com o cancelamento e o estorno ao cliente?\n\nAtenciosamente,\n${assinatura}`
    : `Olá,\n\nInfelizmente, durante a preparação do item ${produto} - ${pedidoParaAssunto} identificamos falha no pedido, o que nos levou a optar pelo cancelamento.\n\nPoderia, por gentileza, seguir com o cancelamento e o estorno ao cliente?\n\nAtenciosamente,\n${assinatura}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-xl mx-4 p-5 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-base flex items-center gap-2">
            <span>📋</span> Template de Cancelamento
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Info do canal */}
        <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-2 text-xs">
          <span className="font-semibold text-slate-700 dark:text-slate-200">{canalVendas || '—'}</span>
          <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">
            {METODO_LABEL[metodo]}
          </span>
        </div>

        <div className="space-y-3">
          {/* Assunto (somente para não-LL) */}
          {assunto && (
            <div className="border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm">📧 Assunto</span>
                <button
                  onClick={() => copiar(assunto, 'Assunto copiado!')}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 font-semibold border border-blue-300 transition-colors"
                >
                  <Copy className="h-3 w-3" /> Copiar
                </button>
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 rounded p-2 font-mono break-all">
                {assunto}
              </div>
            </div>
          )}

          {/* Corpo */}
          <div className="border rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-sm">
                {tipo === 'etr' ? '✉️ Cancelamento TOTAL' : '✉️ Corpo'}
              </span>
              <button
                onClick={() => copiar(tipo === 'etr' ? etrTotal : corpo, 'Texto copiado!')}
                className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 font-semibold border border-blue-300 transition-colors"
              >
                <Copy className="h-3 w-3" /> Copiar
              </button>
            </div>
            <pre className="text-xs text-slate-600 dark:text-slate-300 whitespace-pre-wrap bg-slate-50 dark:bg-slate-800 rounded p-2 font-sans leading-relaxed">
              {tipo === 'etr' ? etrTotal : corpo}
            </pre>
          </div>

          {/* ETR: a versão parcial fica ao lado da total — o registro não sabe
              se os demais itens seguem, então quem trata escolhe. */}
          {tipo === 'etr' && (
            <div className="border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm">✂️ Cancelamento PARCIAL</span>
                <button
                  onClick={() => copiar(etrParcial, 'Texto copiado!')}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 font-semibold border border-blue-300 transition-colors"
                >
                  <Copy className="h-3 w-3" /> Copiar
                </button>
              </div>
              <pre className="text-xs text-slate-600 dark:text-slate-300 whitespace-pre-wrap bg-slate-50 dark:bg-slate-800 rounded p-2 font-sans leading-relaxed">
                {etrParcial}
              </pre>
            </div>
          )}

          {/* Resposta ao canal — o retorno que mandamos quando o canal confirma */}
          <div className="border rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-sm">↩️ Resposta ao canal</span>
              <button
                onClick={() => copiar(respostaCanal, 'Resposta copiada!')}
                className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 font-semibold border border-blue-300 transition-colors"
              >
                <Copy className="h-3 w-3" /> Copiar
              </button>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500 whitespace-nowrap">Falar com:</label>
              <input
                value={nomeContato}
                onChange={e => setNomeContato(e.target.value)}
                placeholder="nome do contato do canal"
                title="Fica lembrado para este canal"
                className="flex-1 text-xs border rounded px-2 py-1 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700"
              />
            </div>
            {item.is_parcial && outrosItens.length === 0 && (
              <div className="text-[11px] text-amber-700 dark:text-amber-400">
                Buscando os demais itens da entrega…
              </div>
            )}
            <pre className="text-xs text-slate-600 dark:text-slate-300 whitespace-pre-wrap bg-slate-50 dark:bg-slate-800 rounded p-2 font-sans leading-relaxed">
              {respostaCanal}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

// =================== MODAL: DADOS DE FATURAMENTO (CPF) ===================
// Os dados que a atendente coleta com o cliente pra subir o pedido novo no CPF.
// Gravam em cliente_confirmado_cpf / _nome / _endereco, que ja existiam no
// registro mas nao tinham tela.
function digitosCpf(v) {
  return String(v || '').replace(/\D/g, '').slice(0, 11);
}

function formataCpf(v) {
  const s = digitosCpf(v);
  if (s.length !== 11) return s;
  return `${s.slice(0, 3)}.${s.slice(3, 6)}.${s.slice(6, 9)}-${s.slice(9)}`;
}

// Validacao real do CPF (digitos verificadores). Nao basta contar 11: digitar
// um numero trocado e subir pedido no documento errado e pior que campo vazio.
function cpfValido(v) {
  const s = digitosCpf(v);
  if (s.length !== 11 || /^(\d)\1{10}$/.test(s)) return false;
  let soma = 0;
  for (let i = 0; i < 9; i++) soma += Number(s[i]) * (10 - i);
  let d1 = (soma * 10) % 11;
  if (d1 === 10) d1 = 0;
  if (d1 !== Number(s[9])) return false;
  soma = 0;
  for (let i = 0; i < 10; i++) soma += Number(s[i]) * (11 - i);
  let d2 = (soma * 10) % 11;
  if (d2 === 10) d2 = 0;
  return d2 === Number(s[10]);
}

function DadosCpfModal({ item, onClose, onSaved }) {
  const { getAuthHeader } = useAuth();
  const [cpf, setCpf] = React.useState(item.cliente_confirmado_cpf || '');
  const [nome, setNome] = React.useState(item.cliente_confirmado_nome || '');
  const [endereco, setEndereco] = React.useState(item.cliente_confirmado_endereco || '');
  const [salvando, setSalvando] = React.useState(false);

  const cpfDig = digitosCpf(cpf);
  const cpfOk = cpfValido(cpf);
  const podeSalvar = cpfOk && nome.trim() && endereco.trim();

  const salvar = async () => {
    if (!podeSalvar) return;
    setSalvando(true);
    try {
      await axios.put(`${API_URL}/api/cancelamentos/${item.id}`, {
        cliente_confirmado_cpf: formataCpf(cpf),
        cliente_confirmado_nome: nome.trim(),
        cliente_confirmado_endereco: endereco.trim(),
      }, { headers: getAuthHeader() });
      toast.success('Dados do faturamento salvos!');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error('Erro ao salvar os dados');
    } finally {
      setSalvando(false);
    }
  };

  const copiarTudo = () => copiar(
    `CPF: ${formataCpf(cpf)}\nNome completo: ${nome}\nEndereço: ${endereco}`,
    'Dados copiados!');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-lg mx-4 p-5 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-base flex items-center gap-2">
            <span>🧑</span> Dados de faturamento no CPF
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="text-xs text-slate-500 bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-2 space-y-0.5">
          <div><strong>Entrega:</strong> {item.numero_pedido}</div>
          {item.produto && <div><strong>Produto:</strong> {item.produto}</div>}
          {item.nome_cliente && <div><strong>Cliente (cadastro):</strong> {item.nome_cliente}</div>}
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">CPF</label>
            <input
              value={cpf}
              onChange={e => setCpf(e.target.value)}
              placeholder="000.000.000-00"
              className={`mt-1 w-full px-3 py-2 text-sm border rounded-lg bg-background font-mono focus:ring-1 ${
                !cpfDig ? 'focus:ring-blue-300'
                  : cpfOk ? 'border-emerald-400 focus:ring-emerald-300'
                  : 'border-red-400 focus:ring-red-300'
              }`}
            />
            <div className="text-[11px] mt-1 h-4">
              {!cpfDig ? <span className="text-slate-400">11 dígitos</span>
                : cpfOk ? <span className="text-emerald-600">✓ {formataCpf(cpf)}</span>
                : <span className="text-red-600">CPF inválido ({cpfDig.length}/11 dígitos)</span>}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">Nome completo</label>
            <input
              value={nome}
              onChange={e => setNome(e.target.value)}
              placeholder="Nome como o cliente informou"
              className="mt-1 w-full px-3 py-2 text-sm border rounded-lg bg-background focus:ring-1 focus:ring-blue-300"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
              Endereço completo com CEP
            </label>
            <textarea
              value={endereco}
              onChange={e => setEndereco(e.target.value)}
              rows={3}
              placeholder="Rua, número, complemento, bairro, cidade/UF, CEP e ponto de referência"
              className="mt-1 w-full px-3 py-2 text-sm border rounded-lg bg-background focus:ring-1 focus:ring-blue-300"
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 pt-1">
          <button
            onClick={copiarTudo}
            disabled={!cpfDig && !nome && !endereco}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 border text-slate-600 disabled:opacity-40"
          >
            <Copy className="h-3 w-3" /> Copiar os dados
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-lg border hover:bg-slate-50">
              Cancelar
            </button>
            <button
              onClick={salvar}
              disabled={!podeSalvar || salvando}
              title={podeSalvar ? '' : 'Preencha CPF válido, nome e endereço'}
              className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-40"
            >
              {salvando ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =================== MODAL: DADOS PRA VTEX ===================
// Campos do formulario "Enviar nota fiscal avulsa" da VTEX, na ordem de la.
// Sempre da ENTREGA NOVA (a que foi subida no CPF).
function VtexModal({ item, onClose }) {
  const { getAuthHeader } = useAuth();
  const nova = String(item.nova_entrega || '').replace(/\D/g, '');
  const [dados, setDados] = React.useState(null);
  const [erro, setErro] = React.useState('');

  React.useEffect(() => {
    if (!nova) { setErro('Preencha a "Nova entrega" primeiro.'); return; }
    axios.get(`${API_URL}/api/cancelamentos/dados-vtex/${nova}`, { headers: getAuthHeader() })
      .then(r => { if (r.data?.ok) setDados(r.data); else setErro(r.data?.message || 'Não encontrado'); })
      .catch(() => setErro('Erro ao buscar os dados da entrega nova'));
  }, []); // eslint-disable-line

  // Uma linha do formulario: rotulo igual ao da VTEX + valor + copiar.
  const Linha = ({ rotulo, valor, mono, link }) => (
    <div className="border rounded-lg px-3 py-2 flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-slate-400">{rotulo}</div>
        {link && valor ? (
          <a href={valor} target="_blank" rel="noreferrer"
             className={`text-xs text-blue-600 hover:underline break-all ${mono ? 'font-mono' : ''}`}>{valor}</a>
        ) : (
          <div className={`text-xs break-all ${mono ? 'font-mono' : ''} ${valor ? '' : 'text-slate-400'}`}>
            {valor || '—'}
          </div>
        )}
      </div>
      {valor && (
        <button
          onClick={() => copiar(valor, `${rotulo} copiado!`)}
          className="shrink-0 inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-slate-100 hover:bg-slate-200 border text-slate-600"
        >
          <Copy className="h-3 w-3" /> copiar
        </button>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-xl mx-4 p-5 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-base flex items-center gap-2">
            <span>🧾</span> Dados pra VTEX — nota fiscal avulsa
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="text-xs text-slate-500 bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-2 space-y-0.5">
          <div><strong>Entrega antiga:</strong> {item.numero_pedido}</div>
          <div><strong>Entrega nova:</strong> {nova || '—'}</div>
          {/* O pedido do cliente é o que identifica o caso no BSeller; quem
              monta a nota avulsa precisa dele à mão, não só das entregas. */}
          {item.pedido_cliente && (
            <div><strong>Pedido:</strong> {item.pedido_cliente}</div>
          )}
          {dados?.cliente && <div><strong>Cliente:</strong> {dados.cliente}</div>}
          {dados?.status_pedido && <div><strong>Status do pedido novo:</strong> {dados.status_pedido}</div>}
        </div>

        {erro && <div className="text-sm text-red-600">{erro}</div>}
        {!erro && !dados && <div className="text-sm text-slate-500">Buscando no tabelão…</div>}

        {dados && !dados.faturado && (
          <div className="text-xs rounded-lg px-3 py-2 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border border-amber-200">
            ⚠️ A entrega nova ainda <strong>não tem nota emitida</strong> — sem chave de acesso não dá
            pra faturar na VTEX. Volte aqui quando o pedido for faturado.
          </div>
        )}

        {dados && (
          <>
            <div className="space-y-2">
              <div className="font-semibold text-sm">Detalhes do faturamento</div>
              <Linha rotulo="Número da nota fiscal" valor={dados.numero_nota} mono />
              <Linha rotulo="URL da nota fiscal" valor={dados.url_nota} />
              <Linha rotulo="Chave de acesso" valor={dados.chave_acesso} mono />
              <Linha rotulo="Data de emissão" valor={dados.data_emissao} />
            </div>
            <div className="space-y-2">
              <div className="font-semibold text-sm">Rastreamento do pedido</div>
              <Linha rotulo="Número de rastreio" valor={dados.numero_rastreio} mono />
              <Linha rotulo="URL de rastreio" valor={dados.url_rastreio} link />
              <Linha rotulo="Transportadora" valor={dados.transportadora
                ? `${dados.transportadora}${dados.filial ? ` (filial ${dados.filial})` : ''}`
                : ''} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// =================== PROPOR SIMILAR (ETR) ===================
// Reusa o modal do Atendimento. O trabalho aqui e sO descobrir DE QUAL ITEM
// estamos falando: o pedidos_erp guarda uma linha por entrega, mas a entrega
// pode ter varios itens (a 125457906 tem 5) — e o similar tem que ser do item
// que faltou.
function ProporSimilarEtr({ item, onClose, onSaved }) {
  const { getAuthHeader } = useAuth();
  const [itens, setItens] = React.useState(null);
  const [erro, setErro] = React.useState('');
  const [sku, setSku] = React.useState('');
  const [confirmado, setConfirmado] = React.useState(false);

  React.useEffect(() => {
    const ent = String(item.numero_pedido || '').replace(/\D/g, '');
    axios.get(`${API_URL}/api/cancelamentos/itens-entrega/${ent}`, { headers: getAuthHeader() })
      .then(r => {
        if (!r.data?.ok) { setErro(r.data?.message || 'não foi possível listar os itens'); return; }
        const lista = r.data.itens || [];
        setItens(lista);
        if (lista.length === 1) {
          setSku(lista[0].cod_terceiro);
          setConfirmado(true);            // item único: não há o que perguntar
        } else {
          // sugere o sem estoque, mas espera o clique
          const zerados = lista.filter(i => i.sem_estoque);
          setSku((zerados[0] || lista[0] || {}).cod_terceiro || '');
        }
      })
      .catch(() => setErro('erro ao consultar os itens da entrega'));
  }, []); // eslint-disable-line

  if (confirmado && sku) {
    const escolhido = (itens || []).find(i => i.cod_terceiro === sku);
    // MESMO modal do cancelamento AES (Adneia, 23/09): a comparação lado a
    // lado — custo, preço, EAN, subcategoria, imagem — é o que deixa decidir
    // se o similar presta. O modal simples do Atendimento listava os nomes e
    // pedia fé. A mensagem pro cliente continua no botão 📋 Texto, ao lado.
    return (
      <SimilarProporModal
        item={{ ...item, codigo_item_vtex: sku, produto: escolhido?.produto || item.produto }}
        onClose={onClose}
        onSaved={onSaved}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-xl mx-4 p-5 space-y-4 max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-base flex items-center gap-2">
            <span>🔄</span> Propor Similar — qual item faltou?
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        {erro && <div className="text-sm text-red-600">{erro}</div>}
        {!erro && !itens && <div className="text-sm text-slate-500">Buscando os itens da entrega…</div>}

        {itens && (
          <>
            <div className="text-xs rounded-lg px-3 py-2 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border border-amber-200">
              ⚠️ <strong>Entrega {item.numero_pedido} tem {itens.length} itens.</strong> Escolha o que
              ficou sem estoque — o similar será buscado só para ele.
            </div>
            <div className="space-y-2">
              {itens.map(i => (
                <label key={i.cod_terceiro}
                  className={`flex items-start gap-2 border rounded-lg p-2 cursor-pointer transition-colors ${
                    sku === i.cod_terceiro ? 'border-blue-400 bg-blue-50/60 dark:bg-blue-950/20' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                  <input type="radio" name="item-etr" className="mt-1"
                         checked={sku === i.cod_terceiro}
                         onChange={() => setSku(i.cod_terceiro)} />
                  <div className="min-w-0">
                    <div className="text-sm leading-tight">{i.produto}</div>
                    <div className="text-[11px] text-slate-500 font-mono">
                      {i.cod_terceiro} · qtd {i.quantidade}
                      {' · '}
                      {i.sem_estoque
                        ? <span className="text-red-600 font-semibold">sem estoque</span>
                        : <span className="text-emerald-700">{i.disponivel} em estoque</span>}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-lg border hover:bg-slate-50">
                Cancelar
              </button>
              <button
                onClick={() => setConfirmado(true)}
                disabled={!sku}
                className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-40"
              >
                Buscar similar deste item
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// =================== MODAL: TEXTOS ERRO NA NOTA ===================
// Os tres textos do fluxo CNPJ->CPF, na ordem em que a operacao usa:
// 1) avisa o problema fiscal e pergunta se pode faturar no CPF;
// 2) pede os dados; 3) agradece e segue pro faturamento.
function TextosErroNotaModal({ item, onClose }) {
  const produto = item.produto || '[produto]';
  const entrega = item.numero_pedido || '[entrega]';
  const fone = foneDigitos(item.fone_cliente);
  const [abertos, setAbertos] = React.useState({ 0: true });
  const toggle = (i) => setAbertos(p => ({ ...p, [i]: !p[i] }));

  const textos = [
    {
      label: '1. Primeiro contato (problema fiscal)',
      emoji: '📄',
      texto: `Olá,\n\nInfelizmente na emissão da Nota fiscal do pedido: ${produto} - ${entrega}, foi constatado um problema fiscal por parte do CNPJ informado o qual impede a aprovação da NF. Gostaria de seguir com o faturamento do pedido no CPF?\n\nSeguimos a disposição.\nAtenciosamente!\nAtendimento Weconnect`,
    },
    {
      label: '2. Pedir os dados do cliente',
      emoji: '📝',
      texto: `Agradecemos o retorno, poderia nos informar:\n\nCPF:\nNome completo:\nConfirmar o endereço completo com CEP:\nPonto de referencia:\n\nAssim poderemos seguir com maior precisão.\n\nSeguimos a disposição.\nAtenciosamente!\nAtendimento Weconnect`,
    },
    {
      label: '3. Agradecer e seguir com o faturamento',
      emoji: '✅',
      texto: `Certo, agradecemos o envio. Seguiremos com o faturamento nos dados informados.\n\nAtenciosamente!\nAtendimento Weconnect`,
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-xl mx-4 p-5 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-base flex items-center gap-2">
            <span>📱</span> Textos — Erro na Nota (CNPJ → CPF)
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="text-xs text-slate-500 bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-2 space-y-0.5">
          <div><strong>Produto:</strong> {produto}</div>
          <div><strong>Entrega:</strong> {entrega}</div>
          {item.nome_cliente && <div><strong>Cliente:</strong> {item.nome_cliente}</div>}
          {item.motivo_rejeicao && <div><strong>Rejeição:</strong> {item.motivo_rejeicao}</div>}
          {fone.length >= 10 && (
            <div className="flex items-center gap-2">
              <strong>Telefone:</strong>
              <a href={`https://wa.me/55${fone}`} target="_blank" rel="noreferrer"
                 className="font-mono text-emerald-700 dark:text-emerald-400 hover:underline">
                📱 {foneBonito(item.fone_cliente)}
              </a>
              <button onClick={() => copiar(fone, 'Telefone copiado!')}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 hover:bg-slate-200 border text-slate-600">
                copiar
              </button>
            </div>
          )}
        </div>

        <div className="space-y-3">
          {textos.map((t, i) => {
            const aberto = !!abertos[i];
            return (
              <div key={i} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <button
                    onClick={() => toggle(i)}
                    className="flex items-center gap-1.5 font-semibold text-sm hover:text-blue-600 transition-colors text-left"
                    title={aberto ? 'Recolher texto' : 'Abrir texto'}
                  >
                    {aberto ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                    {t.emoji} {t.label}
                  </button>
                  <button
                    onClick={() => copiar(t.texto, 'Texto copiado!')}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 hover:bg-green-200 font-semibold border border-green-300 transition-colors whitespace-nowrap"
                  >
                    <Copy className="h-3 w-3" /> Copiar
                  </button>
                </div>
                {aberto && (
                  <pre className="text-xs text-slate-600 dark:text-slate-300 whitespace-pre-wrap bg-slate-50 dark:bg-slate-800 rounded p-2 font-sans leading-relaxed">
                    {t.texto}
                  </pre>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// =================== MODAL: ZAP SIMILAR ===================
function ZapModal({ item, onClose }) {
  const { getAuthHeader } = useAuth();
  const produto = item.produto || '[produto]';
  // ID BSeller do item ORIGINAL dentro do texto, junto do nome — do mesmo jeito que ja
  // aparece nos similares (pedido da Adneia, 15/09). Sem o ID, mantem so o nome.
  const idOriginal = item.codigo_item_bseller || '';
  const produtoComId = idOriginal ? `${produto} (${idOriginal})` : produto;
  const entrega = item.numero_pedido || '[entrega]';
  const skuSimilar = item.sku_similar || '[novo item / SKU]';
  const nomeSimilar = item.nome_similar || skuSimilar;
  const idSimilar = item.id_similar || '';
  const labelSimilar = idSimilar ? `${nomeSimilar} (ID: ${idSimilar})` : nomeSimilar;

  // Controla quais textos estão abertos (default: só a "Mensagem inicial").
  // Em telas menores os textos vinham cortados — agora cada um abre/fecha por botão.
  const [abertos, setAbertos] = React.useState({ 0: true });
  const toggleTexto = (i) => setAbertos(prev => ({ ...prev, [i]: !prev[i] }));
  const [comparativo, setComparativo] = React.useState(false);   // imagem para o cliente

  // Lista de similares propostos (pode ser mais de um). Fallback: o similar único.
  const fmtSim = (s) => {
    const nome = s.nome || s.sku || '[novo item / SKU]';
    return s.id_bseller ? `${nome} (ID: ${s.id_bseller})` : nome;
  };
  const listaSimilares = (item.similares_propostos_detalhe && item.similares_propostos_detalhe.length)
    ? item.similares_propostos_detalhe
    : [{ sku: item.sku_similar || '', nome: item.nome_similar || '', id_bseller: item.id_similar || '' }];
  const multiplosSimilares = listaSimilares.length > 1;

  const skuOriginal = item.codigo_item_vtex || '';

  // Busca lazy de imagens e da página na loja (original + similares). Vai em
  // todos os SKUs, mesmo no similar que já veio com a imagem salva: a página da
  // loja não fica gravada. A imagem salva continua tendo a preferência (imgDe).
  const [imgMap, setImgMap] = React.useState({});
  const [lojaMap, setLojaMap] = React.useState({});   // sku -> página na loja VTEX
  React.useEffect(() => {
    const skusBuscar = [...listaSimilares.map(s => s.sku), skuOriginal];
    [...new Set(skusBuscar.filter(Boolean))].forEach(sk => {
      axios.get(`${API_URL}/api/produtos/imagem/${encodeURIComponent(sk)}`, { headers: getAuthHeader() })
        .then(r => {
          if (r.data?.image_url) setImgMap(prev => ({ ...prev, [sk]: r.data.image_url }));
          if (r.data?.vtex_loja_url) setLojaMap(prev => ({ ...prev, [sk]: r.data.vtex_loja_url }));
        })
        .catch(() => {});
    });
  }, []); // eslint-disable-line
  const imgDe = (s) => s.image_url || imgMap[s.sku] || '';
  const linkOriginal = imgMap[skuOriginal] || '';
  // Na TELA (clique na foto e "Copiar link") vale a página da loja, que tem todos
  // os dados (Adnéia, 13/10/2026); sem ela, a imagem. Os TEXTOS ao cliente
  // continuam com a imagem: a loja pede login da VTEX e o cliente não entra.
  const lojaOriginal = lojaMap[skuOriginal] || '';

  // Custo (último preço de compra) do original + similares — pra decidir o envio
  const [custoMap, setCustoMap] = React.useState({});
  React.useEffect(() => {
    const sk = [skuOriginal, ...listaSimilares.map(s => s.sku)].filter(Boolean);
    if (!sk.length) return;
    axios.get(`${API_URL}/api/cancelamentos/custo-skus?skus=${encodeURIComponent(sk.join(','))}`, { headers: getAuthHeader() })
      .then(r => setCustoMap(r.data?.custos || {}))
      .catch(() => {});
  }, []); // eslint-disable-line
  const custoDe = (s) => { const c = custoMap[(s?.sku || '').toUpperCase()]; return (c != null) ? c : null; };
  const custoOriginal = (custoMap[(skuOriginal || '').toUpperCase()] != null) ? custoMap[(skuOriginal || '').toUpperCase()] : null;
  const linkSufixo = (url) => url ? ` - ${url}` : '';
  const fmtSimComLink = (s) => `${fmtSim(s)}${linkSufixo(imgDe(s))}`;

  // Bloco para a mensagem inicial (plural quando há mais de um), com link da imagem
  const blocoInicial = multiplosSimilares
    ? `Temos como alternativa os seguintes itens similares:\n${listaSimilares.map((s, i) => `${i + 1}) ${fmtSimComLink(s)}`).join('\n')}\nPoderia confirmar se aceita a substituição por um deles?`
    : `Temos como alternativa um item similar: ${fmtSimComLink(listaSimilares[0])}. Poderia confirmar se aceita a substituição pelo item similar?`;
  // Bloco para "cliente aceita"
  const blocoAceita = multiplosSimilares
    ? `os novos itens:\n${listaSimilares.map((s, i) => `${i + 1}) ${fmtSimComLink(s)}`).join('\n')}`
    : `o novo item - ${fmtSimComLink(listaSimilares[0])}`;

  // Alerta de incompatibilidade: verifica se produto e similar têm palavras em comum
  const STOPWORDS = new Set(['de','da','do','em','com','para','e','a','o','um','uma','os','as','na','no','por','se']);
  const palavras = (txt) => txt.toLowerCase().split(/\W+/).filter(w => w.length > 3 && !STOPWORDS.has(w));
  const similarIncompativel = (() => {
    if (!item.nome_similar || !item.produto) return false;
    const pProd = new Set(palavras(item.produto));
    const pSim  = palavras(item.nome_similar);
    return !pSim.some(w => pProd.has(w)); // nenhuma palavra em comum
  })();

  const templates = [
    {
      label: 'Mensagem inicial',
      emoji: '💬',
      texto: `Boa tarde\nInfelizmente, tivemos uma falha sistêmica no item ${produtoComId} - ${entrega}${linkSufixo(linkOriginal)}\n${blocoInicial}\nAguardamos retorno e seguimos à disposição.\nAtenciosamente!\nAtendimento Weconnect`,
    },
    {
      label: 'Cliente não aceita',
      emoji: '❌',
      texto: `Agradecemos a confirmação, Iremos acionar o canal de troca/venda para estornar os valores pagos. Nossas sinceras desculpas pelo ocorrido.\nAtenciosamente!\nAtendimento Weconnect`,
    },
    {
      label: 'Cliente aceita',
      emoji: '✅',
      texto: `Agradecemos a confirmação, seguiremos com a preparação d${multiplosSimilares ? 'os' : 'o'} ${blocoAceita}.\nNossas sinceras desculpas pelo ocorrido.\nAtenciosamente!\nAtendimento Weconnect`,
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-xl mx-4 p-5 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-base flex items-center gap-2">
            <span>📱</span> Templates WhatsApp — Similar
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="text-xs text-slate-500 bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-2 space-y-0.5">
          <div><strong>Produto:</strong> {produto}</div>
          <div><strong>Entrega:</strong> {entrega}</div>
          {item.canal_vendas && <div><strong>Canal:</strong> {item.canal_vendas}</div>}
          {item.nome_cliente && <div><strong>Cliente:</strong> {item.nome_cliente}</div>}
          {(() => {
            const foneRaw = item.fone_cliente || '';
            const foneDigits = foneRaw.replace(/\D/g, '');
            if (!foneDigits) return null;
            return (
              <div className="flex items-center gap-2">
                <strong>Telefone:</strong>
                <button
                  onMouseEnter={() => { try { navigator.clipboard?.writeText(foneRaw); } catch {} }}
                  onClick={() => copiar(foneRaw, 'Telefone copiado!')}
                  title="Passe o mouse (ou clique) para copiar o número"
                  className="font-mono hover:text-blue-600 hover:underline cursor-pointer"
                >{foneRaw}</button>
              </div>
            );
          })()}
          <div className="flex items-start gap-1">
            <strong>{multiplosSimilares ? 'Similares:' : 'Similar:'}</strong>
            {multiplosSimilares ? (
              <span>{listaSimilares.map((s, i) => <span key={s.sku} className="block">{i + 1}) {fmtSim(s)}</span>)}</span>
            ) : (
              <span>{labelSimilar}</span>
            )}
            {similarIncompativel && (
              <span
                title="Atenção: O produto proposto como similar, possui uma descrição incompatível com o produto adquirido. Confirme antes de oferecer."
                className="cursor-help text-amber-500"
              >⚠️</span>
            )}
          </div>
        </div>

        {/* Imagens + custo: original x similar(es) — comparar custo e decidir o envio */}
        {(linkOriginal || custoOriginal != null || listaSimilares.some(s => imgDe(s) || custoDe(s) != null)) && (
          <div className="space-y-1">
            <div className="text-xs font-semibold text-slate-600">🖼️ Comparação (clique para abrir na loja / copiar link):</div>
            <div className="flex flex-wrap gap-3 items-start">
              {/* Original */}
              {(linkOriginal || custoOriginal != null) && (
                <div className="flex flex-col items-center gap-1">
                  <span className="text-[10px] font-semibold text-slate-500">ORIGINAL</span>
                  {linkOriginal ? (
                    <FotoLink img={linkOriginal} loja={lojaOriginal} alt={produto}
                      className="w-24 h-24 object-contain border-2 border-slate-300 rounded bg-white" />
                  ) : (
                    <div className="w-24 h-24 flex items-center justify-center border-2 border-dashed border-slate-200 rounded text-[10px] text-slate-400">sem imagem</div>
                  )}
                  {custoOriginal != null && (
                    <span className="text-[11px] font-semibold text-slate-700">Custo: {formatMoney(custoOriginal)}</span>
                  )}
                  {(lojaOriginal || linkOriginal) && (
                    <button onClick={() => copiar(lojaOriginal || linkOriginal, lojaOriginal ? 'Link da loja (original) copiado!' : 'Link do original copiado!')}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 hover:bg-slate-200 border text-slate-600">📋 Copiar link{lojaOriginal ? ' da loja' : ''}</button>
                  )}
                </div>
              )}
              {(linkOriginal || custoOriginal != null) && listaSimilares.some(s => imgDe(s) || custoDe(s) != null) && (
                <div className="self-center text-2xl text-slate-300">→</div>
              )}
              {/* Similares */}
              {listaSimilares.filter(s => imgDe(s) || custoDe(s) != null).map((s) => {
                const cs = custoDe(s);
                const dif = (cs != null && custoOriginal != null) ? Math.round((cs - custoOriginal) * 100) / 100 : null;
                return (
                <div key={s.sku} className="flex flex-col items-center gap-1">
                  <span className="text-[10px] font-semibold text-green-600">SIMILAR</span>
                  {imgDe(s) ? (
                    <FotoLink img={imgDe(s)} loja={lojaMap[s.sku]} alt={s.nome}
                      className="w-24 h-24 object-contain border-2 border-green-300 rounded bg-white" />
                  ) : (
                    <div className="w-24 h-24 flex items-center justify-center border-2 border-dashed border-green-200 rounded text-[10px] text-slate-400">sem imagem</div>
                  )}
                  {cs != null && (
                    <span className="text-[11px] font-semibold text-slate-700">
                      Custo: {formatMoney(cs)}
                      {dif != null && (
                        <span className={dif > 0 ? 'text-red-600' : dif < 0 ? 'text-emerald-600' : ''}>
                          {' '}({dif >= 0 ? '+' : '−'}{formatMoney(Math.abs(dif))})
                        </span>
                      )}
                    </span>
                  )}
                  {(lojaMap[s.sku] || imgDe(s)) && (
                    <button onClick={() => copiar(lojaMap[s.sku] || imgDe(s), lojaMap[s.sku] ? 'Link da loja (similar) copiado!' : 'Link do similar copiado!')}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 hover:bg-slate-200 border text-slate-600">📋 Copiar link{lojaMap[s.sku] ? ' da loja' : ''}</button>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-3">
          {/* Imagem do comparativo para anexar no WhatsApp: original x os similares propostos */}
          <button onClick={() => setComparativo(true)} disabled={!skuOriginal || !listaSimilares.some(s => s.sku)}
            title={skuOriginal ? 'Gerar a imagem com o original e os similares propostos' : 'Linha sem o SKU do original: não dá para montar a imagem'}
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 font-semibold border border-blue-300 disabled:opacity-50">
            🖼️ Imagem do comparativo
          </button>
          {templates.map((t, i) => {
            const aberto = !!abertos[i];
            return (
              <div key={i} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <button
                    onClick={() => toggleTexto(i)}
                    className="flex items-center gap-1.5 font-semibold text-sm hover:text-blue-600 transition-colors"
                    title={aberto ? 'Recolher texto' : 'Abrir texto'}
                  >
                    {aberto ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    {t.emoji} {t.label}
                  </button>
                  <button
                    onClick={() => copiar(t.texto, `"${t.label}" copiado!`)}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 hover:bg-green-200 font-semibold border border-green-300 transition-colors"
                  >
                    <Copy className="h-3 w-3" /> Copiar
                  </button>
                </div>
                {aberto && (
                  <pre className="text-xs text-slate-600 dark:text-slate-300 whitespace-pre-wrap bg-slate-50 dark:bg-slate-800 rounded p-2 font-sans leading-relaxed">
                    {t.texto}
                  </pre>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {comparativo && (
        <ComparativoCliente
          original={{ sku: skuOriginal, nome: produto, id: idOriginal }}
          similares={listaSimilares.filter(s => s.sku).map(s => ({ sku: s.sku, nome: s.nome || s.sku, id: s.id_bseller }))}
          entrega={item.numero_pedido || ''}
          onClose={() => setComparativo(false)}
        />
      )}
    </div>
  );
}

// =================== TABELA POR TIPO ===================
function TabelaCancelamentos({ tipo, refreshKey, onRefresh }) {
  const { getAuthHeader, user } = useAuth();
  // Ações (alternar Similar/Cancelar, excluir, priorizar): admin E atendente.
  // 'consulta' (somente leitura) e 'dashboard' não têm.
  const isAdmin = ['admin', 'atendente'].includes(user?.role) || user?.email === 'adneia@weconnect360.com.br';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('pendente'); // pendente | encerrado | todos
  const [filtroParceiro, setFiltroParceiro] = useState('');
  // ?entrega=NNN — é assim que o link vindo do Tem em Casa cai na linha certa
  const [filtroEntrega, setFiltroEntrega] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('entrega') || '';
    } catch { return ''; }
  });
  const [filtroProduto, setFiltroProduto] = useState('');
  // ?canal=X&ticket=aberto — o link da linha Ag. Parceiro do Dashboard (Adnéia, 21/10/2026)
  const [filtroCanal, setFiltroCanal] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('canal') || ''; } catch { return ''; }
  });
  const [filtroTicket, setFiltroTicket] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('ticket') === 'aberto'; } catch { return false; }
  });
  const [filtroPendencia, setFiltroPendencia] = useState('');
  // Tipo da acao (Adnéia, 23/09): Propor Similar e Cancelar tem prazos diferentes, entao
  // a tela precisa separar os dois. 'todos' | 'similar' | 'cancelar'.
  const [filtroAcao, setFiltroAcao] = useState('todos');
  // entrega -> caso do Tem em Casa (só AES; nos outros tipos não se aplica)
  const [temCasaMap, setTemCasaMap] = useState({});
  const [verCompras, setVerCompras] = useState(false);  // true = visualiza só os em_compras
  const [filtroNovos, setFiltroNovos] = useState(false); // true = só os recém-chegados (novo=true)
  const [showNovo, setShowNovo] = useState(false);
  const [zapModal, setZapModal] = React.useState(null);
  const [textosErroNota, setTextosErroNota] = React.useState(null);
  const [vtexModal, setVtexModal] = React.useState(null);
  const [cpfModal, setCpfModal] = React.useState(null);
  const [similarEtr, setSimilarEtr] = React.useState(null);
  const [textoModal, setTextoModal] = React.useState(null);
  const [proporModal, setProporModal] = React.useState(null);

  const togglePrioridade = async (item) => {
    try {
      await axios.put(
        `${API_URL}/api/cancelamentos/${item.id}`,
        { prioridade: !item.prioridade },
        { headers: getAuthHeader() }
      );
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, prioridade: !item.prioridade } : i));
    } catch (e) {
      alert('Erro prioridade: ' + (e?.response?.data?.detail || e?.message || 'erro desconhecido'));
    }
  };

  const toggleEmCompras = async (item) => {
    const novo = !item.em_compras;
    try {
      await axios.put(
        `${API_URL}/api/cancelamentos/${item.id}`,
        { em_compras: novo },
        { headers: getAuthHeader() }
      );
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, em_compras: novo } : i));
      toast.success(novo ? '→ Movido para Compras' : '← Voltou para o canal');
    } catch (e) {
      toast.error('Erro: ' + (e?.response?.data?.detail || e?.message || 'erro desconhecido'));
    }
  };

  // Alterna a ação deste registro entre Similar e Cancelar (somente este, não os irmãos do mesmo SKU).
  // No ETR a ação pode ser 'Cancelar/Reenviar' ou 'Reenviar'; por isso a de antes
  // fica guardada e volta como estava, em vez de virar 'Cancelar' seco.
  const toggleAcao = async (item) => {
    const eraSimilar = (item.acao || '').toLowerCase().includes('similar');
    const patch = eraSimilar
      ? { acao: item.acao_antes_similar || 'Cancelar', analise_similar: 'cancelar' }
      : { acao: 'Similar', analise_similar: 'pendente', acao_antes_similar: item.acao || '' };
    try {
      await axios.put(`${API_URL}/api/cancelamentos/${item.id}`, patch, { headers: getAuthHeader() });
      toast.success(eraSimilar ? '→ Alterado para Cancelamento' : '→ Alterado para Similar');
      patchItemLocal(item.id, patch);   // a linha muda agora, não só no "Atualizar"
      marcarAlterado();
    } catch (e) {
      toast.error('Erro: ' + (e?.response?.data?.detail || e?.message || 'erro desconhecido'));
    }
  };

  // Exclui o cancelamento (uso administrativo, com confirmação).
  const excluirCancelamento = async (item) => {
    if (!window.confirm(`Excluir o cancelamento da entrega ${item.numero_pedido}?\nEsta ação não pode ser desfeita.`)) return;
    try {
      await axios.delete(`${API_URL}/api/cancelamentos/${item.id}`, { headers: getAuthHeader() });
      setItems(prev => prev.filter(i => i.id !== item.id));
      toast.success('Cancelamento excluído');
      onRefresh?.();
    } catch (e) {
      toast.error('Erro ao excluir: ' + (e?.response?.data?.detail || e?.message || 'erro desconhecido'));
    }
  };

  // A lista NAO recarrega sozinha a cada registro (pedido da Adneia, 15/09): antes cada
  // save disparava fetchItems() + onRefresh() — duas recargas — e a rolagem voltava pro
  // topo no meio do preenchimento. O valor digitado ja fica salvo (o PUT acontece) e
  // visivel na celula; aqui so contamos as alteracoes pra avisar que vale Atualizar.
  const [alterados, setAlterados] = useState(0);
  const marcarAlterado = useCallback(() => setAlterados(n => n + 1), []);
  // Atualiza UMA linha na tela sem ir no servidor (a lista so recarrega no botao Atualizar).
  const patchItemLocal = useCallback((id, patch) => {
    setItems(prev => prev.map(it => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { tipo, limit: 1000 };
      if (filtro !== 'todos') params.status = filtro;
      const res = await axios.get(`${API_URL}/api/cancelamentos`, { params, headers: getAuthHeader() });
      setItems(res.data?.cancelamentos || []);
      setAlterados(0);
    } catch (e) {
      toast.error('Erro ao carregar');
    } finally { setLoading(false); }
  }, [tipo, filtro, getAuthHeader]);

  useEffect(() => { fetchItems(); }, [fetchItems, refreshKey]);

  // Importa a devolução do Compras (arquivo da Relação Compras com Retorno + Status)
  const importarRetorno = async (file) => {
    try {
      toast.info('Lendo o retorno do Compras...');
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
      const rows = data.map(r => ({
        entrega: String(r['Entrega'] ?? r['Entrega Ped'] ?? r['entrega'] ?? '').split('.')[0].trim(),
        retorno: String(r['Retorno Compras'] ?? r['Retorno'] ?? '').trim(),
        status: String(r['Status'] ?? r['status'] ?? '').trim(),
      })).filter(r => r.entrega && (r.retorno || r.status));
      if (!rows.length) {
        toast.warning('Arquivo sem linhas válidas — precisa das colunas Entrega, Retorno Compras e Status.');
        return;
      }
      const res = await axios.post(`${API_URL}/api/cancelamentos/importar-retorno`, { rows }, { headers: getAuthHeader() });
      const s = res.data?.stats || {};
      toast.success(`Retorno importado: ${s.encerrados || 0} encerrados · ${s.mantidos || 0} mantidos · ${s.notas || 0} notas${s.nao_encontrados ? ` · ${s.nao_encontrados} não encontrados` : ''}`);
      fetchItems();
    } catch (e) {
      console.error(e);
      toast.error('Erro ao importar o retorno do Compras.');
    }
  };

  // Detecta se é "Similar": baseado APENAS no campo acao (não na observação)

  const isSimilar = (item) => {
    const acao = (item.acao || '').toLowerCase();
    return acao.includes('similar');
  };

  // Parceiros únicos com itens NA ABA ATIVA (para os botões de filtro rápido)
  // - pendente: só pendentes | encerrado: só encerrados | todos: todos
  const parceirosFiltrados = React.useMemo(() => {
    const counts = {};
    items.forEach(i => {
      if (filtro === 'pendente' && i.status === 'encerrado') return;
      if (filtro === 'encerrado' && i.status !== 'encerrado') return;
      const p = i.canal_vendas || i.parceiro_planilha;
      if (!p) return;
      counts[p] = (counts[p] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]); // maior quantidade primeiro
  }, [items, filtro]);

  // Filtrar + ordenar:
  //   PRIMEIRO: Similar antes de Cancelamento (similar sempre no topo)
  //   DEPOIS, 4 grupos:
  //     1 — Já acionado parceiros (tem ticket)
  //     2 — Tem atendimento mas sem ticket (acionar a seguir)
  //     3 — Falta acionar, valor ≤ R$300 (menos urgente)
  //     4 — Falta acionar, valor > R$300 (maior valor - no fim)
  //   POR FIM: data asc (mais antigo no topo, mais novo no final)
  const itemsExibidos = React.useMemo(() => {
    let arr = items;
    // Quando há BUSCA ativa (entrega / produto / canal), varre TODAS as caixas
    // (normal + Compras) — não esconde em_compras. Assim o pedido é encontrado em
    // qualquer caixa e a coluna/linha indica onde ele está.
    const buscandoAlgo = !!(filtroEntrega.trim() || filtroProduto.trim() || filtroCanal.trim());
    if (buscandoAlgo) {
      // busca global: não filtra por caixa
    } else if (verCompras) {
      // Caixa Compras: filtra só os em_compras=true (ignora outros filtros de parceiro)
      arr = arr.filter(i => i.em_compras === true);
    } else {
      // Modo normal: esconde os que foram movidos para Compras
      arr = arr.filter(i => !i.em_compras);
      if (filtroParceiro) {
        arr = arr.filter(i => (i.canal_vendas || i.parceiro_planilha) === filtroParceiro);
      }
    }
    if (filtroEntrega.trim()) {
      const q = filtroEntrega.trim();
      arr = arr.filter(i => String(i.numero_pedido || '').includes(q));
    }
    if (filtroProduto.trim()) {
      const q = filtroProduto.trim().toLowerCase();
      arr = arr.filter(i =>
        String(i.codigo_item_bseller || '').toLowerCase().includes(q) ||
        String(i.codigo_item_vtex || '').toLowerCase().includes(q) ||
        String(i.produto || '').toLowerCase().includes(q)
      );
    }
    if (filtroCanal.trim()) {
      const q = filtroCanal.trim().toLowerCase();
      arr = arr.filter(i => String(i.canal_vendas || i.parceiro_planilha || '').toLowerCase().includes(q));
    }
    if (filtroTicket) {
      // Cancelamento esperando o parceiro: a mesma régua da linha Ag. Parceiro do Dashboard
      // (routes/dashboard.py): ticket do canal preenchido e ação de cancelamento.
      arr = arr.filter(i => String(i.ticket || '').trim() && /cancel/i.test(i.acao || ''));
    }
    if (filtroPendencia) {
      // mesma regra da célula: manual manda, senão vale a sugestão do sistema
      arr = arr.filter(i => (i.pendencia || i.pendencia_auto || 'Ag. tratativa') === filtroPendencia);
    }
    if (filtroAcao !== 'todos') {
      arr = arr.filter(i => (filtroAcao === 'similar') === isSimilar(i));
    }
    // Filtro "Novos": só os que chegaram pela migração automática e ainda não foram
    // triados (novo=true). Some quando o analista propõe similar ou segue cancelamento.
    if (filtroNovos) {
      arr = arr.filter(i => i.novo === true);
    }
    // Ordem da lista (definida com a Adneia). A DECISÃO DE SIMILAR é CONGELADA:
    // quando o item entra na faixa "Similar" (entrou_similar), ele NÃO cai pro fim ao
    // ser decidido cancelar — fica na mesma posição pra não sumir de vista enquanto
    // é tratado. Prioridade, ticket, atendimento, estoque XD e >30 dias seguem ao vivo
    // (podem SUBIR o item — só a queda do similar->cancelar é que é evitada).
    // (Vale pro ETR. O AES usa ordem de chegada desde 06/10 — ver mais abaixo.)
    //   2 priorizado · 3 similar(congelado) · 4 com ticket ·
    //   5 com atendimento · 6 parado >30d · 7 cancelamento ≤R$300 · 8 cancelamento >R$300
    const rankOf = (i) => {
      if (i.prioridade) return 2;
      const foiSimilar = i.entrou_similar
        || i.analise_similar === 'pendente' || i.analise_similar === 'proposto'
        || (i.acao || '').toLowerCase().includes('similar');
      if (foiSimilar) return 3;
      if (i.ticket && String(i.ticket).trim()) return 4;
      if (i.tem_atendimento) return 5;
      if (diasPendentes(i) > 30) return 6;
      const valor = Number(String(i.preco_final || '0').replace(',', '.'));
      return valor > 300 ? 8 : 7;
    };
    // Na aba Encerrados: ordena apenas por data_encerramento DESC (mais recente fechado no topo)
    if (filtro === 'encerrado') {
      return [...arr].sort((a, b) => {
        const da = a.data_encerramento || '';
        const db = b.data_encerramento || '';
        return db.localeCompare(da);
      });
    }
    // Erro na Nota: sem fila de prioridade. O que manda e a espera do cliente,
    // entao e do mais antigo pro mais novo (Adneia, 14/09). Registro sem data
    // vai pro fim, nao pro topo.
    if (tipo === 'erro_nota') {
      return [...arr].sort((a, b) =>
        String(a.data || '9999-99-99').localeCompare(String(b.data || '9999-99-99')));
    }
    // AES: ordem de CHEGADA, mais antigo primeiro (Adneia, 06/10). So sobe quem
    // estiver com a estrela de prioridade; o resto e data pura. Substitui as 8
    // faixas e o agrupamento por SKU, que embaralhavam as datas na tela — tres
    // calculadoras do mesmo SKU ficavam juntas (31/07, 31/07, 21/09) e o pedido
    // de 21/09 aparecia antes do de 04/08. No mesmo dia, desempata pela hora em
    // que o registro entrou, pra ordem nao pular entre um carregamento e outro.
    // Sem data vai pro fim, como no Erro na Nota.
    if (tipo === 'aes') {
      return [...arr].sort((a, b) => {
        const pa = a.prioridade ? 0 : 1;
        const pb = b.prioridade ? 0 : 1;
        if (pa !== pb) return pa - pb;              // estrela primeiro
        const da = String(a.data || '9999-99-99');
        const db = String(b.data || '9999-99-99');
        if (da !== db) return da.localeCompare(db);
        return String(a.data_criacao || '').localeCompare(String(b.data_criacao || ''));
      });
    }
    const ordenado = [...arr].sort((a, b) => {
      const ra = rankOf(a), rb = rankOf(b);
      if (ra !== rb) return ra - rb;
      // dentro da mesma faixa: mais antigo primeiro (data ASC)
      return String(a.data || '').localeCompare(String(b.data || ''));
    });
    return ordenado;
  }, [items, filtroParceiro, filtroEntrega, filtroProduto, filtroCanal, filtroTicket, filtroPendencia, filtroAcao, filtro, tipo, verCompras, filtroNovos]); // eslint-disable-line react-hooks/exhaustive-deps

  // TEM EM CASA: marca as entregas que têm a peça parada numa filial nossa.
  // Consulta só as linhas exibidas e só no AES — é onde a decisão de cancelar
  // acontece (Adneia, 17/09).
  useEffect(() => {
    if (tipo !== 'aes' || !itemsExibidos.length) return;
    const ents = [...new Set(itemsExibidos
      .filter(i => i.status !== 'encerrado')
      .map(i => String(i.numero_pedido || '').replace(/\D/g, ''))
      .filter(Boolean))].slice(0, 60);
    const faltando = ents.filter(e => temCasaMap[e] === undefined);
    if (!faltando.length) return;
    let vivo = true;
    Promise.all(faltando.map(e =>
      axios.get(`${API_URL}/api/tem-em-casa/check/${e}`, { headers: getAuthHeader() })
        .then(r => [e, r.data?.has_caso ? r.data : null])
        .catch(() => [e, null])
    )).then(pares => {
      if (!vivo) return;
      setTemCasaMap(prev => ({ ...prev, ...Object.fromEntries(pares) }));
    });
    return () => { vivo = false; };
  }, [itemsExibidos, tipo]); // eslint-disable-line

  // colunas: Dias, Data, Entrega, Parceiro, Cliente, Produto, [XD AES | Motivo ETR | MotivoRejeicao err], Valor, Ticket, Instância, [Template AES | Nova entrega err], Encerrado, Observação
  // erro_nota ganhou 2 colunas (Dados CPF + Status da nova entrega) em 14/09
  const colSpan = tipo === 'aes' ? 14 : tipo === 'etr' ? 13 : 15;

  return (
    <div className="space-y-3">
      {/* Filtros de status + ação */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2">
          <Button size="sm" variant={filtro === 'pendente' ? 'default' : 'outline'} onClick={() => { setFiltro('pendente'); setFiltroParceiro(''); setFiltroNovos(false); }}>
            Pendentes
          </Button>
          <Button size="sm" variant={filtro === 'encerrado' ? 'default' : 'outline'} onClick={() => { setFiltro('encerrado'); setFiltroParceiro(''); }}>
            Encerrados
          </Button>
          {tipo === 'aes' && (
            <>
              <span className="w-px bg-border mx-1" aria-hidden="true" />
              <Button size="sm" variant={filtroAcao === 'similar' ? 'default' : 'outline'}
                title="Veio do Smart Compras como 'já tenho similar' — prazo de 2 dias úteis"
                onClick={() => setFiltroAcao(filtroAcao === 'similar' ? 'todos' : 'similar')}>
                Propor similar
              </Button>
              <Button size="sm" variant={filtroAcao === 'cancelar' ? 'default' : 'outline'}
                title="Cancelamento para analisarmos — prazo de 15 dias"
                onClick={() => setFiltroAcao(filtroAcao === 'cancelar' ? 'todos' : 'cancelar')}>
                Cancelar
              </Button>
              <Button size="sm" variant={filtroTicket ? 'default' : 'outline'}
                title="Cancelamentos com o ticket do canal preenchido, esperando o parceiro (entram na lista Ag. Parceiro da quarta)"
                onClick={() => setFiltroTicket(v => !v)}>
                Ticket aberto{filtroTicket ? ' ✕' : ''}
              </Button>
            </>
          )}
          <Button size="sm" variant={filtro === 'todos' ? 'default' : 'outline'} onClick={() => { setFiltro('todos'); setFiltroParceiro(''); }}>
            Todos
          </Button>
        </div>
        <div className="flex items-center gap-2">
          {tipo === 'aes' && (
            <Button size="sm" variant="outline"
              onClick={() => copiar(
                'Boa tarde, recebemos para cancelamento na planilha de AET os itens em anexo, porém consta estoque na data de hoje. Poderiam verificar se seguimos com o cancelamento?',
                'Texto copiado!'
              )}
              className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300">
              📋 Texto Compras
            </Button>
          )}
          {tipo === 'aes' && (
            <Button size="sm" variant="outline" onClick={() => baixarRelacaoComprasXLSX(getAuthHeader)}
              className="bg-red-50 hover:bg-red-100 text-red-700 border-red-300">
              ⚠️ Relação Compras
            </Button>
          )}
          {tipo === 'aes' && (
            <label
              className="inline-flex items-center gap-1 text-sm font-medium h-9 px-3 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 cursor-pointer"
              title="Importar o arquivo devolvido pelo Compras (Relação Compras com Retorno Compras + Status)">
              ⬆️ Importar Retorno
              <input type="file" accept=".xlsx,.xls,.csv" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) importarRetorno(f); e.target.value = ''; }} />
            </label>
          )}
          <Button size="sm" onClick={() => setShowNovo(true)}>
            <Plus className="h-4 w-4 mr-1" /> Novo
          </Button>
        </div>
      </div>

      {/* Botões rápidos por parceiro — aparecem em qualquer aba (pendente / encerrado / todos) */}
      {(parceirosFiltrados.length > 0 || tipo === 'aes') && (
        <div className="flex items-center gap-2 flex-wrap py-1">
          <span className="text-xs text-slate-500 font-medium mr-1">Filtrar parceiro:</span>
          {/* Botão "Novos" — recém-chegados pela migração automática (novo=true) */}
          {tipo === 'aes' && (() => {
            const qtdNovos = items.filter(i => i.novo === true && i.status !== 'encerrado').length;
            return (
              <button
                type="button"
                onClick={() => { setFiltroNovos(v => !v); setVerCompras(false); setFiltroParceiro(''); }}
                className={`text-xs px-2.5 py-1 rounded-full border font-semibold transition-colors ${
                  filtroNovos
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100'
                }`}
                title="Cancelamentos que chegaram pela migração automática e ainda não foram analisados"
              >
                🆕 Novos
                <span className={`ml-1 px-1.5 py-0 rounded ${filtroNovos ? 'bg-white/20' : 'bg-emerald-200'}`}>{qtdNovos}</span>
              </button>
            );
          })()}
          {/* Botão "Compras" — só pra AES */}
          {tipo === 'aes' && (() => {
            const qtdCompras = items.filter(i => i.em_compras === true && (filtro === 'todos' ? true : (filtro === 'encerrado' ? i.status === 'encerrado' : i.status !== 'encerrado'))).length;
            return (
              <button
                type="button"
                onClick={() => { setVerCompras(v => !v); setFiltroParceiro(''); setFiltroNovos(false); }}
                className={`text-xs px-2.5 py-1 rounded-full border font-semibold transition-colors ${
                  verCompras
                    ? 'bg-orange-600 text-white border-orange-600'
                    : 'bg-orange-50 text-orange-700 border-orange-300 hover:bg-orange-100'
                }`}
                title="Cancelamentos AES movidos para a caixa Compras"
              >
                🛒 Compras
                <span className={`ml-1 px-1.5 py-0 rounded ${verCompras ? 'bg-white/20' : 'bg-orange-200'}`}>{qtdCompras}</span>
              </button>
            );
          })()}
          {!verCompras && parceirosFiltrados.map(([nome, qtd]) => (
            <button
              key={nome}
              type="button"
              onClick={() => { setFiltroParceiro(filtroParceiro === nome ? '' : nome); setFiltroNovos(false); }}
              className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${
                filtroParceiro === nome
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              {nome} <span className={`ml-1 px-1.5 py-0 rounded ${filtroParceiro === nome ? 'bg-white/20' : 'bg-slate-200 dark:bg-slate-700'}`}>{qtd}</span>
            </button>
          ))}
          {filtroParceiro && (
            <button
              type="button"
              onClick={() => setFiltroParceiro('')}
              className="text-xs px-2 py-1 text-slate-500 hover:text-slate-700"
            >
              ✕ limpar
            </button>
          )}
        </div>
      )}

      {/* Indicador de filtros ativos */}
      {(filtroParceiro || filtroEntrega || filtroProduto || filtroCanal || filtroPendencia) && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>Mostrando {itemsExibidos.length} de {items.length}</span>
          <Button size="sm" variant="ghost" onClick={() => { setFiltroParceiro(''); setFiltroEntrega(''); setFiltroProduto(''); setFiltroCanal(''); setFiltroPendencia(''); }}>
            <X className="h-4 w-4 mr-1" /> Limpar filtros
          </Button>
        </div>
      )}

      {/* Tabela com edição inline */}
      {/* META do AES (Adnéia, 23/09): o prazo depende de COMO o caso chegou.
          Propor Similar vem do Smart Compras ja resolvido pelo fornecedor — e so ofertar,
          por isso 2 dias uteis. Cancelar exige analise nossa, por isso 15 dias.
          "Ag. Bseller" fica de fora nos dois casos: a espera e externa. */}
      {tipo === 'aes' && (() => {
        const P_SIMILAR = 2, P_CANCELAR = 15;
        const duteis = (dataStr) => {
          const m = String(dataStr || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (!m) return null;
          const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
          const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
          let n = 0;
          while (d < hoje) { d.setDate(d.getDate() + 1); const w = d.getDay(); if (w !== 0 && w !== 6) n++; }
          return n;
        };
        const dcorridos = (dataStr) => {
          const m = String(dataStr || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (!m) return null;
          const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
          const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
          return Math.max(0, Math.floor((hoje - d) / 86400000));
        };
        const pend = (items || []).filter(i => i.status !== 'encerrado');
        const bsel = pend.filter(i => (i.pendencia || i.pendencia_auto) === 'Ag. Bseller');
        const aval = pend.filter(i => (i.pendencia || i.pendencia_auto) !== 'Ag. Bseller');
        const foraSim = aval.filter(i => isSimilar(i) && (duteis(i.data) || 0) > P_SIMILAR);
        const foraCan = aval.filter(i => !isSimilar(i) && (dcorridos(i.data) || 0) > P_CANCELAR);
        const fora = [...foraSim, ...foraCan];
        const ok = fora.length === 0;
        return (
          <div className="mb-2 rounded-lg border border-orange-200 dark:border-orange-900 bg-orange-50/70 dark:bg-orange-950/20 px-3 py-2">
            <div className="text-[11px] font-bold text-orange-700 dark:text-orange-300">
              META · Propor similar em {P_SIMILAR} dias úteis · Cancelar em {P_CANCELAR} dias. "Ag. Bseller" não conta.
            </div>
            <div className={`text-sm font-semibold mt-0.5 ${ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
              {ok
                ? `✅ meta atingida — ${aval.length} pendente(s) avaliado(s), todos dentro do prazo`
                : `⚠️ ${fora.length} de ${aval.length} pendente(s) fora do prazo` +
                  (foraSim.length ? ` · ${foraSim.length} Propor similar (${P_SIMILAR}d úteis)` : '') +
                  (foraCan.length ? ` · ${foraCan.length} Cancelar (${P_CANCELAR}d)` : '')}
              {bsel.length ? ` · ${bsel.length} aguardando BSeller (fora do SLA)` : ''}
            </div>
          </div>
        );
      })()}
      {/* META do ETR (Adnéia, 23/09): pendente com estoque tem 2 dias; sem estoque, 5.
          Calculada aqui com o que a lista já carregou — estoque_xd_disp vem do backend. */}
      {tipo === 'etr' && (() => {
        // Duas etapas, em dias UTEIS (Adnéia, 23/09): 2 para INICIAR a tratativa e 5 para
        // FINALIZAR. "Iniciou" = a pendencia saiu de 'Ag. tratativa' (que significa
        // literalmente "ninguem olhou ainda").
        const PRAZO_INICIO = 2, PRAZO_FIM = 5;
        const diasUteisDesde = (dataStr) => {
          const m = String(dataStr || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (!m) return null;
          const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
          const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
          let n = 0;
          while (d < hoje) {
            d.setDate(d.getDate() + 1);
            const dw = d.getDay();
            if (dw !== 0 && dw !== 6) n++;
          }
          return n;
        };
        const pendentes = (items || []).filter(i => i.status !== 'encerrado');
        // "Ag. Bseller" sai da conta (Adnéia, 23/09): a espera e do outro lado, o time nao
        // tem como destravar. Continua aparecendo no contador para nao sumir da vista.
        const aguardandoBseller = pendentes.filter(i => (i.pendencia || i.pendencia_auto) === 'Ag. Bseller');
        const avaliados = pendentes.filter(i => (i.pendencia || i.pendencia_auto) !== 'Ag. Bseller');
        const naoIniciados = avaliados.filter(i => {
          const du = diasUteisDesde(i.data);
          return (i.pendencia || i.pendencia_auto) === 'Ag. tratativa' && du !== null && du > PRAZO_INICIO;
        });
        const naoFinalizados = avaliados.filter(i => {
          const du = diasUteisDesde(i.data);
          return du !== null && du > PRAZO_FIM;
        });
        const estourados = avaliados.filter(i => naoIniciados.includes(i) || naoFinalizados.includes(i));
        const ok = estourados.length === 0;
        const sufixoBseller = aguardandoBseller.length
          ? ` · ${aguardandoBseller.length} aguardando BSeller (fora do SLA)` : '';
        return (
          <div className="mb-2 rounded-lg border border-orange-200 dark:border-orange-900 bg-orange-50/70 dark:bg-orange-950/20 px-3 py-2">
            <div className="text-[11px] font-bold text-orange-700 dark:text-orange-300">
              META · {PRAZO_INICIO} dias úteis para iniciar a tratativa e {PRAZO_FIM} dias úteis para finalizar. "Ag. Bseller" não conta no prazo.
            </div>
            <div className={`text-sm font-semibold mt-0.5 ${ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
              {ok
                ? `✅ meta atingida — ${avaliados.length} pendente(s) avaliado(s), todos dentro do prazo` + sufixoBseller
                : `⚠️ ${estourados.length} de ${avaliados.length} pendente(s) fora do prazo` +
                  (naoIniciados.length ? ` · ${naoIniciados.length} sem tratativa iniciada (${PRAZO_INICIO}d úteis)` : '') +
                  (naoFinalizados.length ? ` · ${naoFinalizados.length} sem finalizar (${PRAZO_FIM}d úteis)` : '') + sufixoBseller}
            </div>
            {!ok && (
              <div className="mt-1 flex flex-wrap gap-1">
                {estourados.slice(0, 12).map(i => {
                  const du = diasUteisDesde(i.data);
                  const semInicio = naoIniciados.includes(i);
                  return (
                    <span key={i.id}
                      title={`${i.produto || ''} · ${du} dia(s) útil(eis) · ${semInicio ? 'tratativa não iniciada' : 'não finalizado'}`}
                      className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200 whitespace-nowrap">
                      {i.numero_pedido} · {du}d úteis{semInicio ? ' (sem tratativa)' : ''}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}
      {alterados > 0 && (
        <div className="mb-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 rounded-md px-3 py-1.5">
          {alterados} alteração(ões) salva(s). A lista só recarrega quando você clicar em <strong>Atualizar</strong>.
        </div>
      )}
      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-900/30 border-b">
            <tr className="text-left">
              <th className="px-2 py-2 font-medium text-slate-600 w-20 text-center">Tipo / Dias</th>
              <th className="px-2 py-2 font-medium text-slate-600 whitespace-nowrap">Data</th>
              <th className="px-2 py-2 font-medium text-slate-600 w-[110px]">
                <div>Entrega</div>
                <input
                  type="text"
                  placeholder="🔍 filtrar..."
                  value={filtroEntrega}
                  onChange={e => setFiltroEntrega(e.target.value)}
                  className="mt-1 w-full px-2 py-1 text-xs font-normal border rounded bg-background focus:ring-1 focus:ring-blue-300"
                />
              </th>
              <th className="px-2 py-2 font-medium text-slate-600 w-[110px]">
                <div>Parceiro</div>
                <input
                  type="text"
                  placeholder="🔍 canal..."
                  value={filtroCanal}
                  onChange={e => setFiltroCanal(e.target.value)}
                  className="mt-1 w-full px-2 py-1 text-xs font-normal border rounded bg-background focus:ring-1 focus:ring-blue-300"
                />
              </th>
              <th className="px-2 py-2 font-medium text-slate-600">Cliente</th>
              <th className="px-2 py-2 font-medium text-slate-600 max-w-[260px]">
                <div>Produto</div>
                <input
                  type="text"
                  placeholder="🔍 ID / SKU / nome..."
                  value={filtroProduto}
                  onChange={e => setFiltroProduto(e.target.value)}
                  className="mt-1 w-full px-2 py-1 text-xs font-normal border rounded bg-background focus:ring-1 focus:ring-blue-300"
                />
              </th>
              {tipo === 'aes' && <th className="px-2 py-2 font-medium text-slate-600 w-16 text-center" title="Estoque cross-dock (SIGEQ425)">XD</th>}
              {tipo === 'etr' && <th className="px-2 py-2 font-medium text-slate-600">Motivo</th>}
              {tipo === 'erro_nota' && <th className="px-2 py-2 font-medium text-slate-600">Motivo Rejeição</th>}
              <th className="px-2 py-2 font-medium text-slate-600 text-right whitespace-nowrap">Valor</th>
              {tipo === 'erro_nota' ? (
                <>
                  {/* Ordem pedida pela Adneia (14/09): os dados do CPF vêm antes
                      da nova entrega, e o ticket/instância depois dela. */}
                  <th className="px-2 py-2 font-medium text-slate-600 w-28">Dados p/ faturar (CPF)</th>
                  <th className="px-2 py-2 font-medium text-slate-600 w-28">Nova entrega</th>
                  <th className="px-2 py-2 font-medium text-slate-600 w-32">Ticket</th>
                  <th className="px-2 py-2 font-medium text-slate-600 w-28">Instância</th>
                  <th className="px-2 py-2 font-medium text-slate-600 w-32">Status da nova entrega</th>
                </>
              ) : (
                <>
                  <th className="px-2 py-2 font-medium text-slate-600 w-32">Ticket</th>
                  <th className="px-2 py-2 font-medium text-slate-600 w-28">Instância</th>
                  {tipo === 'aes' && <th className="px-2 py-2 font-medium text-slate-600 w-32">Template</th>}
                </>
              )}
              <th className="px-2 py-2 font-medium text-slate-600 w-32">
                <div>Pendência</div>
                <select
                  value={filtroPendencia}
                  onChange={e => setFiltroPendencia(e.target.value)}
                  className="mt-1 w-full px-1 py-1 text-xs font-normal border rounded bg-background focus:ring-1 focus:ring-blue-300"
                >
                  <option value="">todas</option>
                  {pendenciasDe(tipo).map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </th>
              <th className="px-2 py-2 font-medium text-slate-600 min-w-[320px]">Observação</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={colSpan} className="px-3 py-8 text-center text-slate-400">Carregando...</td></tr>
            ) : itemsExibidos.length === 0 ? (
              <tr><td colSpan={colSpan} className="px-3 py-8 text-center text-slate-400">Nenhum cancelamento</td></tr>
            ) : itemsExibidos.map(item => {
              const valor = Number(String(item.preco_final || '0').replace(',', '.'));
              const valorAlto = valor > 300;
              const similar = isSimilar(item);
              const isParcial = !!item.is_parcial;
              const isSicredi = (item.canal_vendas || item.parceiro_planilha || '').toLowerCase().includes('sicredi');
              return (
              <tr key={item.id} className={`border-b hover:bg-slate-50/30 dark:hover:bg-slate-900/10 ${item.status === 'encerrado' ? 'opacity-60' : ''} ${valorAlto && item.status !== 'encerrado' ? 'bg-red-50/40 dark:bg-red-950/10' : ''}`}>
                <td className="px-2 py-2 text-center">
                  <div className="flex flex-col items-center gap-0.5">
                    <StatusBadge item={item} />
                    {similar ? (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200 font-semibold uppercase tracking-wide whitespace-nowrap">
                        🔄 {isParcial ? 'Similar Parcial' : 'Similar'}
                      </span>
                    ) : (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200 font-semibold uppercase tracking-wide whitespace-nowrap">
                        ✕ {isParcial ? 'Cancelar Parcial' : 'Cancelar'}
                      </span>
                    )}
                    {item.suspenso && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700 border border-slate-400 font-semibold uppercase tracking-wide whitespace-nowrap"
                        title="Suspenso — Compras informou data de faturamento (produto vai chegar). Aguardando o Smart Compras redefinir cancelar/similar.">
                        ⏸ Suspenso{item.suspenso_data ? ` · ${String(item.suspenso_data).split('-').reverse().join('/')}` : ''}
                      </span>
                    )}
                    {/* O atendimento foi encerrado, mas a entrega nunca foi
                        cancelada na Bseller e ainda dava pra cancelar. Antes
                        o registro simplesmente fechava (caso 125392011). */}
                    {item.alerta_nao_cancelado && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-400 font-semibold uppercase tracking-wide whitespace-nowrap"
                        title="O atendimento foi encerrado, mas a entrega segue ativa na Bseller. Confira a instância e o cancelamento — o automático não encerra este registro.">
                        ⚠️ Não cancelado
                      </span>
                    )}
                    {item.reaberto_manual && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-300 font-semibold uppercase tracking-wide whitespace-nowrap"
                        title={`Reaberto na mão${item.reaberto_por ? ` por ${item.reaberto_por}` : ''}. O encerramento automático não fecha este registro — encerre pela pendência quando terminar.`}>
                        ↩ Reaberto
                      </span>
                    )}
                    {isParcial && isSicredi && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-800 border border-yellow-300 font-semibold whitespace-nowrap" title="SICREDI não aceita cancelamento parcial">
                        ⚠️ SICREDI
                      </span>
                    )}
                    {item.prioridade && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-300 font-semibold whitespace-nowrap">
                        ⭐ Priorizado
                      </span>
                    )}
                    {isAdmin && (
                      <input
                        type="checkbox"
                        checked={!!item.prioridade}
                        onChange={() => togglePrioridade(item)}
                        title={item.prioridade ? 'Remover prioridade' : 'Priorizar este item'}
                        className="mt-1 h-3.5 w-3.5 cursor-pointer accent-blue-600"
                      />
                    )}
                    {isAdmin && (
                      <div className="mt-1 flex items-center gap-1">
                        {(tipo === 'aes' || tipo === 'etr') && (
                          <button
                            type="button"
                            onClick={() => toggleAcao(item)}
                            title={similar
                              ? 'Cliente não aceitou o similar — alterar para Cancelamento'
                              : 'Alterar para Similar (propor)'}
                            className={`w-6 h-6 flex items-center justify-center rounded-full border text-xs font-bold transition-colors ${similar ? 'bg-orange-50 text-orange-600 border-orange-300 hover:bg-orange-100' : 'bg-purple-50 text-purple-600 border-purple-300 hover:bg-purple-100'}`}
                          >
                            {similar ? '✕' : '🔄'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => excluirCancelamento(item)}
                          title="Excluir este cancelamento"
                          className="w-6 h-6 flex items-center justify-center rounded-full border bg-red-50 text-red-600 border-red-200 hover:bg-red-100 transition-colors"
                        >
                          🗑️
                        </button>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-2 py-2 text-sm whitespace-nowrap text-slate-600">{formatData(item.data)}</td>
                <td className="px-2 py-2 font-mono text-sm">
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => copiar(item.numero_pedido, 'Entrega copiada!')}
                      className="hover:text-blue-600 inline-flex items-center gap-1"
                    >
                      {item.numero_pedido} <Copy className="h-3 w-3 opacity-50" />
                    </button>
                    {/* Erro na Nota: os números que a atendente usa fora do ELO —
                        o do parceiro (LTM-xxxx) pra falar com o canal e o do
                        cliente pra achar o pedido no BSeller. */}
                    {tipo === 'erro_nota' && item.pedido_externo && (
                      <button
                        onClick={() => copiar(item.pedido_externo, 'Pedido do parceiro copiado!')}
                        title="Pedido no parceiro — clique para copiar"
                        className="text-[10px] text-slate-500 hover:text-blue-600 w-fit"
                      >{item.pedido_externo}</button>
                    )}
                    {tipo === 'erro_nota' && item.pedido_cliente && (
                      <button
                        onClick={() => copiar(item.pedido_cliente, 'Pedido do cliente copiado!')}
                        title="Pedido do cliente (BSeller) — clique para copiar"
                        className="text-[10px] text-slate-500 hover:text-blue-600 w-fit"
                      >ped. {item.pedido_cliente}</button>
                    )}
                    {item.novo && (
                      <span className="text-[10px] px-1.5 py-0 rounded-full bg-emerald-100 text-emerald-700 font-semibold w-fit"
                        title="Chegou pela migração automática — ainda não analisado">🆕 Novo</span>
                    )}
                    {/* Cancelamento acima do limite: segura até a Adnéia liberar
                        pelo e-mail (Adneia, 17/09). */}
                    {item.aprovacao_status === 'aguardando' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-300 font-semibold w-fit whitespace-nowrap"
                        title="Cancelamento acima do limite — enviado para aprovação por e-mail. Não seguir até liberar.">
                        ⏸️ aguardando aprovação
                      </span>
                    )}
                    {item.aprovacao_status === 'aprovado' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300 font-semibold w-fit whitespace-nowrap"
                        title={`Liberado por ${item.aprovado_por || '—'}${item.aprovado_em ? ' em ' + String(item.aprovado_em).slice(0, 10) : ''}`}>
                        ✅ liberado
                      </span>
                    )}
                    {/* A peça está numa filial nossa: cancelar aqui joga fora
                        uma venda que dá pra salvar (Adneia, 17/09). */}
                    {(() => {
                      const tc = temCasaMap[String(item.numero_pedido || '').replace(/\D/g, '')];
                      if (!tc) return null;
                      return (
                        <a
                          href={tc.url}
                          target="_blank"
                          rel="noreferrer"
                          title={`TEM EM CASA — ${tc.tipo_label} · ${tc.status_label}. Clique para abrir.`}
                          className={`text-[10px] px-1.5 py-0 rounded-full font-semibold w-fit whitespace-nowrap ${
                            tc.em_aberto
                              ? 'bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200'
                              : 'bg-slate-100 text-slate-600 border border-slate-300 hover:bg-slate-200'}`}
                        >📦 Tem em Casa ↗</a>
                      );
                    })()}
                    {item.tem_atendimento && (
                      <span
                        className="text-[10px] text-blue-700 dark:text-blue-400 font-semibold inline-flex items-center gap-1"
                        title={`Solicitação: ${item.solicitacao_atendimento || '—'} · Atendimento: ${item.id_atendimento || '—'}`}
                      >
                        🎫 {item.solicitacao_atendimento || 'Atendimento aberto'}
                      </span>
                    )}
                    {(() => {
                      const al = alertaStatusCamila(tipo, item);
                      if (!al) return null;
                      return (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800 border border-red-400 font-semibold w-fit max-w-[180px] leading-tight dark:bg-red-950/40 dark:text-red-300 dark:border-red-700"
                          title={`Status atual no Bseller: ${al.atual}`}>
                          ⚠️ Pedido não está em {al.rotulo} — acionar Camila
                          <span className="block font-normal">agora: {al.atual}</span>
                        </span>
                      );
                    })()}
                    {/* Caixa onde o pedido está (útil na busca global, que varre as duas) */}
                    <span
                      className={`text-[10px] font-semibold inline-flex items-center gap-1 w-fit px-1.5 py-0.5 rounded ${item.em_compras ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'}`}
                      title={item.em_compras ? 'Caixa 🛒 Compras (movido para Compras)' : 'Lista normal (por canal)'}
                    >
                      {item.em_compras ? '🛒 Compras' : '📋 Normal'}
                    </span>
                  </div>
                </td>
                <td className="px-2 py-2 text-sm">{item.canal_vendas || item.parceiro_planilha || '—'}</td>
                <td className="px-2 py-2 text-sm">
                  <div>{item.nome_cliente || '—'}</div>
                  {/* Zap do cliente: so no Erro na Nota, onde a atendente precisa
                      confirmar o CPF antes de resubir o pedido. */}
                  {tipo === 'erro_nota' && (
                    foneDigitos(item.fone_cliente).length >= 10 ? (
                      <a
                        href={`https://wa.me/55${foneDigitos(item.fone_cliente)}`}
                        target="_blank"
                        rel="noreferrer"
                        title="Abrir conversa no WhatsApp"
                        className="text-[11px] text-emerald-700 dark:text-emerald-400 hover:underline whitespace-nowrap"
                      >📱 {foneBonito(item.fone_cliente)}</a>
                    ) : (
                      <span className="text-[11px] text-slate-400">sem telefone</span>
                    )
                  )}
                  {tipo === 'erro_nota' && item.email_cliente && (
                    <div>
                      <button
                        onClick={() => copiar(item.email_cliente, 'E-mail copiado!')}
                        title={`${item.email_cliente} — clique para copiar`}
                        className="text-[10px] text-slate-500 hover:text-blue-600 max-w-[160px] truncate block text-left"
                      >✉️ {item.email_cliente}</button>
                    </div>
                  )}
                  {tipo === 'erro_nota' && (
                    <button
                      onClick={() => setTextosErroNota(item)}
                      title="Textos padrão do fluxo CNPJ → CPF"
                      className="mt-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold border bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-300 whitespace-nowrap"
                    >
                      📄 Textos
                    </button>
                  )}
                </td>
                <td className="px-2 py-2 text-sm max-w-[260px]">
                  <button
                    onClick={() => abrirProdutoNaLoja(item.codigo_item_vtex, getAuthHeader)}
                    title="Clique para abrir o produto na loja (VTEX)"
                    className="leading-tight text-left hover:text-blue-600 hover:underline cursor-pointer"
                  >{item.produto || '—'}</button>
                  {item.codigo_item_vtex && (
                    <button
                      onClick={() => copiar(item.codigo_item_vtex, `SKU ${item.codigo_item_vtex} copiado!`)}
                      title="Clique para copiar o SKU"
                      className="block text-[10px] text-slate-400 font-mono mt-0.5 hover:text-blue-600 hover:underline cursor-pointer"
                    >{item.codigo_item_vtex}</button>
                  )}
                </td>
                {tipo === 'aes' && (() => {
                  const xd = Number(item.estoque_xd_disp || 0);
                  const emCompras = item.em_compras === true;
                  // Em Compras: badge laranja com seta de voltar
                  if (emCompras) {
                    return (
                      <td className="px-2 py-2 text-center">
                        <button
                          onClick={() => toggleEmCompras(item)}
                          title="Em Compras — clique para voltar ao canal"
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-400 font-bold text-xs hover:bg-orange-200"
                        >
                          🛒 {xd}
                        </button>
                      </td>
                    );
                  }
                  if (xd > 10) {
                    return (
                      <td className="px-2 py-2 text-center">
                        <button
                          onClick={() => toggleEmCompras(item)}
                          title={`Estoque ${xd} — clique para mover para a caixa Compras`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-300 font-bold text-xs hover:bg-red-200"
                        >
                          ⚠️ {xd}
                        </button>
                      </td>
                    );
                  }
                  if (xd > 0) {
                    return <td className="px-2 py-2 text-center text-xs text-slate-500">{xd}</td>;
                  }
                  return <td className="px-2 py-2 text-center text-xs text-slate-400">0</td>;
                })()}
                {tipo === 'etr' && <td className="px-2 py-2 text-sm">{item.motivo || '—'}</td>}
                {tipo === 'erro_nota' && <td className="px-2 py-2 text-sm max-w-[200px] truncate" title={item.motivo_rejeicao}>{item.motivo_rejeicao || '—'}</td>}
                <td className={`px-2 py-2 text-right font-bold whitespace-nowrap text-sm ${valorAlto ? 'text-red-600' : 'text-emerald-700'}`}>
                  {formatMoney(item.preco_final)}
                </td>
                {tipo === 'erro_nota' && (
                  <td className="px-2 py-2">
                    {(() => {
                      const temCpf = !!(item.cliente_confirmado_cpf || '').trim();
                      const completo = temCpf && (item.cliente_confirmado_nome || '').trim()
                        && (item.cliente_confirmado_endereco || '').trim();
                      return (
                        <button
                          onClick={() => setCpfModal(item)}
                          title={completo ? 'Dados coletados — clique para ver/editar'
                            : temCpf ? 'Faltam dados' : 'Preencher os dados que o cliente informou'}
                          className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold border whitespace-nowrap ${
                            completo
                              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-emerald-300'
                              : temCpf
                              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 border-amber-300'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border-slate-300'
                          }`}
                        >
                          {completo ? '✓ CPF' : temCpf ? '⚠️ CPF' : '＋ CPF'}
                        </button>
                      );
                    })()}
                    {(item.cliente_confirmado_cpf || '').trim() && (
                      <div className="text-[10px] text-slate-500 font-mono mt-0.5 whitespace-nowrap">
                        {item.cliente_confirmado_cpf}
                      </div>
                    )}
                  </td>
                )}
                {tipo === 'erro_nota' && (
                  <td className="px-2 py-2">
                    <CellInput item={item} field="nova_entrega" placeholder="—" onSaved={marcarAlterado} />
                    {/* Com a entrega nova preenchida, os campos da VTEX ficam a um clique. */}
                    {String(item.nova_entrega || '').replace(/\D/g, '').length >= 6 && (
                      <button
                        onClick={() => setVtexModal(item)}
                        title="Chave de acesso, nota, data e rastreio pra dar faturado na VTEX"
                        className="mt-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold border bg-purple-100 text-purple-700 hover:bg-purple-200 border-purple-300 whitespace-nowrap"
                      >
                        🧾 VTEX
                      </button>
                    )}
                  </td>
                )}
                <td className="px-2 py-2">
                  <CellInput item={item} field="ticket" placeholder="—" onSaved={marcarAlterado} />
                  {/* Trava dos 10 dias do AES (Adnéia, 21/10/2026). A gestão libera antes, com o motivo. */}
                  {tipo === 'aes' && (() => {
                    const libera = aesCancelarAPartirDe(item);
                    if (!libera) return null;
                    // O aviso aparece no caminho de cancelar sem ticket; o "Liberar antes" também
                    // no Similar que já tem ticket, que não pode virar Cancelar antes dos 10 dias
                    // (ressalva 1 da revisão 4).
                    const temTicket = !!String(item.ticket || '').trim();
                    const mostraAviso = !temTicket && !isSimilar(item);
                    // Com ticket e já em Cancelar não há nada a liberar (miudeza 1 da revisão 5).
                    const podeLiberar = user?.role === 'admin' && (mostraAviso || (temTicket && !/cancel/i.test(item.acao || '')));
                    if (!mostraAviso && !podeLiberar) return null;
                    const dataBr = libera.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
                    const liberar = async () => {
                      const motivo = (window.prompt('Motivo para liberar o cancelamento antes dos 10 dias:') || '').trim();
                      if (!motivo) return;
                      try {
                        const r = await axios.post(`${API_URL}/api/cancelamentos/${item.id}/liberar-antes`,
                          { motivo }, { headers: getAuthHeader() });
                        const c = r.data?.cancelamento || {};
                        patchItemLocal(item.id, { liberado_antes_em: c.liberado_antes_em, liberado_antes_por: c.liberado_antes_por, observacao: c.observacao });
                        toast.success('Liberado para cancelar antes dos 10 dias');
                        marcarAlterado();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'Não liberou. Tente de novo.');
                      }
                    };
                    return (
                      <div className="mt-1 flex flex-col gap-0.5">
                        {mostraAviso && (
                          <span title="O AES fica 10 dias corridos na tela esperando chegar ao estoque ou um similar; o ticket de cancelamento só grava depois."
                            className="text-[10px] font-semibold text-amber-700 dark:text-amber-300 whitespace-nowrap">
                            ⏳ Cancelar a partir de {dataBr}
                          </span>
                        )}
                        {podeLiberar && (
                          <button type="button" onClick={liberar}
                            className="text-[10px] px-1.5 py-0.5 rounded border border-amber-300 text-amber-800 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-950/40 whitespace-nowrap">
                            Liberar antes
                          </button>
                        )}
                      </div>
                    );
                  })()}
                  {tipo === 'aes' && item.liberado_antes_em && (
                    <span title={item.liberado_antes_motivo || ''}
                      className="mt-1 block text-[10px] text-slate-500 whitespace-nowrap">
                      liberado antes por {item.liberado_antes_por || '?'}
                    </span>
                  )}
                  {/* O caminho depende do canal, igual ao AES: Livelo, Sicredi e
                      ShopHub se cancelam direto no portal; os outros por texto.
                      O botão dizia sempre "Texto" mesmo abrindo a tela do portal. */}
                  {tipo === 'etr' && (() => {
                    const metodo = getCanalMetodo(item.canal_vendas || item.parceiro_planilha || '');
                    const ehPortal = metodo === 'portal';
                    return (
                      <button
                        onClick={() => setTextoModal(item)}
                        title={ehPortal
                          ? `Cancelamento direto no portal ${item.canal_vendas || ''} — dados do pedido`
                          : 'Texto de cancelamento (total ou parcial) no canal certo'}
                        className={`mt-1 mr-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold border whitespace-nowrap ${
                          ehPortal
                            ? 'bg-slate-100 text-slate-600 hover:bg-slate-200 border-slate-300'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-300'}`}
                      >
                        {ehPortal ? '🌐 Portal' : '📋 Texto'}
                      </button>
                    );
                  })()}
                  {/* Mesmo par do AES: enquanto nao ha similar escolhido, o botao
                      e "Propor"; depois de propor, vira o Zap (a mensagem com a
                      oferta) e um atalho pra trocar a escolha. Sem isso o clique
                      em Propor gravava e a linha ficava igual — parecia perdido. */}
                  {tipo === 'etr' && item.analise_similar === 'cancelar' && !similar ? (
                    // Já foi decidido cancelar (cliente recusou o similar). Some o
                    // "Propor" pra não convidar a refazer o caminho — pra voltar
                    // atrás existe o alternador 🔄 na primeira coluna.
                    <span className="mt-1 block text-[10px] text-orange-600 font-semibold whitespace-nowrap">
                      ✕ cliente recusou o similar
                    </span>
                  ) : tipo === 'etr' ? (similar ? (
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      <button
                        onClick={() => setZapModal(item)}
                        title={`Mensagem de similar pro cliente${item.sku_similar ? ` (${item.sku_similar})` : ''}`}
                        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold border bg-green-100 text-green-700 hover:bg-green-200 border-green-300 whitespace-nowrap"
                      >
                        📱 Zap
                      </button>
                      <button
                        onClick={() => setSimilarEtr(item)}
                        title="Rever a comparação e trocar o similar proposto"
                        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold border bg-slate-100 text-slate-600 hover:bg-slate-200 border-slate-300 whitespace-nowrap"
                      >
                        🔄 Trocar
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setSimilarEtr(item)}
                      title="Buscar similar para este item (mesmo motor do Atendimento)"
                      className="mt-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold border bg-green-100 text-green-700 hover:bg-green-200 border-green-300 whitespace-nowrap"
                    >
                      🔄 Propor Similar
                    </button>
                  )) : null}
                </td>
                <td className="px-2 py-2">
                  <CellInput item={item} field="instancia" placeholder="—" onSaved={marcarAlterado} />
                </td>
                {tipo === 'erro_nota' && (
                  <td className="px-2 py-2 text-xs">
                    {(() => {
                      const st = item.status_nova_entrega || '';
                      if (!String(item.nova_entrega || '').trim()) {
                        return <span className="text-slate-400">—</span>;
                      }
                      if (!st) return <span className="text-slate-400">buscando…</span>;

                      // situação vem do backend: ok | atencao | problema
                      const sit = item.nova_entrega_situacao || 'atencao';
                      const estilo = {
                        ok: 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300',
                        atencao: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300',
                        problema: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300',
                      }[sit];
                      const icone = { ok: '✅', atencao: '⏳', problema: '🚨' }[sit];
                      const explica = {
                        ok: 'Pedido novo andando.',
                        atencao: 'Pedido novo PARADO no início do fluxo — acompanhar.',
                        problema: 'Pedido novo com PROBLEMA — precisa de ação.',
                      }[sit];
                      const dica = item.nova_entrega_faturada
                        ? `${explica} Nota ${item.nota_nova_entrega || ''} emitida — dá pra faturar na VTEX.`
                        : `${explica} Ainda sem nota emitida.`;
                      return (
                        <div className="flex flex-col gap-0.5">
                          {/* o número vem junto: sem ele o badge parece falar do
                              pedido antigo da linha */}
                          <span className="text-[10px] text-slate-500 font-mono whitespace-nowrap">
                            entrega {String(item.nova_entrega || '').trim()}
                          </span>
                          <span
                            title={dica}
                            className={`inline-flex w-fit items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] font-medium leading-tight ${estilo}`}
                          >
                            {icone} {st}
                          </span>
                          {(() => {
                            // "desde quando": vem como '11/09/2026 16:24:34' ou ISO
                            const bruto = String(item.data_status_nova_entrega || '');
                            if (!bruto) return null;
                            let dia = '', hora = '';
                            const br = bruto.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}:\d{2}))?/);
                            const iso = bruto.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}:\d{2}))?/);
                            if (br) { dia = `${br[1]}/${br[2]}`; hora = br[4] || ''; }
                            else if (iso) { dia = `${iso[3]}/${iso[2]}`; hora = iso[4] || ''; }
                            else return null;
                            return (
                              <span className="text-[10px] text-slate-500 whitespace-nowrap"
                                    title={`Neste status desde ${bruto}`}>
                                desde {dia}{hora ? ` ${hora}` : ''}
                              </span>
                            );
                          })()}
                          {item.nova_entrega_faturada && (
                            <span className="text-[10px] text-emerald-700 dark:text-emerald-400 whitespace-nowrap">
                              🧾 nota {item.nota_nova_entrega || ''}
                            </span>
                          )}
                          {/* Galpão de onde a nova entrega sai. Em amarelo porque
                              é o que muda a cobrança: quem acompanha precisa saber
                              para qual filial ligar antes de acionar. */}
                          {item.filial_nova_entrega && (
                            <span
                              title={`Galpão de saída da nova entrega: ${item.filial_nova_entrega}`}
                              className="inline-flex w-fit items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold leading-tight whitespace-nowrap bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300"
                            >🏭 {item.filial_nova_entrega}</span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                )}
                {tipo === 'aes' && (
                  <td className="px-2 py-2">
                    {/* Regra (Adneia): todo item entra na REVISÃO de similar. Enquanto o
                        analista não decidir (proposto/cancelar), mostra a caixa "Propor"
                        — com sugestões automáticas OU busca manual de SKU — e o botão
                        "seguir cancelamento". Só vai pro texto de cancelamento quando NÃO
                        houver similar e o analista confirmar. */}
                    {(!similar && item.analise_similar !== 'cancelar') ? (
                      <button
                        onClick={() => setProporModal(item)}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-semibold border bg-green-100 text-green-700 hover:bg-green-200 border-green-300 whitespace-nowrap"
                      >
                        🔄 Propor Similar
                      </button>
                    ) : similar ? (
                      <div className="flex flex-col gap-1">
                        <SimilarLookupCell item={item} onSaved={marcarAlterado} />

                        {(() => {
                          const STOP = new Set(['de','da','do','em','com','para','e','a','o','um','uma','os','as','na','no','por','se']);
                          const pw = (t) => (t||'').toLowerCase().split(/\W+/).filter(w => w.length > 3 && !STOP.has(w));
                          const incompat = item.nome_similar && item.produto && !pw(item.nome_similar).some(w => new Set(pw(item.produto)).has(w));
                          return (
                            <button
                              onClick={() => setZapModal(item)}
                              title={incompat ? "Atenção: O produto proposto como similar, possui uma descrição incompatível com o produto adquirido. Confirme antes de oferecer." : undefined}
                              className={`inline-flex items-center justify-center gap-1 text-xs px-2 py-1 rounded-full font-semibold border transition-colors whitespace-nowrap ${incompat ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 border-amber-400' : 'bg-green-100 text-green-700 hover:bg-green-200 border-green-300'}`}
                            >
                              {incompat ? '⚠️' : '📱'} Zap
                            </button>
                          );
                        })()}
                      </div>
                    ) : (
                      (() => {
                        const metodo = getCanalMetodo(item.canal_vendas || item.parceiro_planilha || '');
                        return (
                          <button
                            onClick={() => setTextoModal(item)}
                            className={`inline-flex items-center justify-center gap-1 text-xs px-2 py-1 rounded-full font-semibold border transition-colors whitespace-nowrap ${
                              metodo === 'portal'
                                ? 'bg-slate-100 text-slate-600 hover:bg-slate-200 border-slate-300'
                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-300'
                            }`}
                          >
                            {metodo === 'portal' ? '🌐 Portal' : '📋 Texto'}
                          </button>
                        );
                      })()
                    )}
                  </td>
                )}
                <td className="px-2 py-2 w-32">
                  <PendenciaCell item={item} tipo={tipo} onSaved={marcarAlterado} onPatchLocal={patchItemLocal} />
                </td>
                <td className="px-2 py-2 min-w-[320px]">
                  <CellTextarea item={item} field="observacao" placeholder="Adicionar nota..." onSaved={marcarAlterado} />
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <NovoCancelamentoDialog
        open={showNovo}
        onClose={() => setShowNovo(false)}
        tipo={tipo}
        onCreated={() => { fetchItems(); onRefresh?.(); }}
      />

      {zapModal && <ZapModal item={zapModal} onClose={() => setZapModal(null)} />}
      {textosErroNota && (
        <TextosErroNotaModal item={textosErroNota} onClose={() => setTextosErroNota(null)} />
      )}
      {vtexModal && <VtexModal item={vtexModal} onClose={() => setVtexModal(null)} />}
      {similarEtr && (
        <ProporSimilarEtr
          item={similarEtr}
          onClose={() => setSimilarEtr(null)}
          onSaved={(gravado) => {
            if (gravado) patchItemLocal(similarEtr.id, gravado);
            marcarAlterado();
            setSimilarEtr(null);
          }}
        />
      )}
      {cpfModal && (
        <DadosCpfModal
          item={cpfModal}
          onClose={() => setCpfModal(null)}
          onSaved={marcarAlterado}
        />
      )}
      {textoModal && <TextoModal item={textoModal} tipo={tipo} onClose={() => setTextoModal(null)} />}
      {proporModal && (
        <SimilarProporModal
          item={proporModal}
          onClose={() => setProporModal(null)}
          onSaved={(gravado) => {
            // corrige a linha na hora; sem isto so mudava depois de "Atualizar"
            if (gravado) patchItemLocal(proporModal.id, gravado);
            marcarAlterado();
            setProporModal(null);
          }}
        />
      )}
    </div>
  );
}

// =================== EXPORT DIRETO: RELAÇÃO COMPRAS XLSX ===================
async function baixarRelacaoComprasXLSX(getAuthHeader) {
  // Gerado no backend (openpyxl): inclui a coluna "Retorno Compras" e destaca em
  // AMARELO os itens já avaliados pelo Compras (que ainda constam com estoque).
  try {
    const r = await axios.get(`${API_URL}/api/cancelamentos/relacao-compras-xlsx`,
      { headers: getAuthHeader(), responseType: 'blob' });
    const cd = r.headers['content-disposition'] || '';
    const m = /filename="?([^"]+)"?/.exec(cd);
    const fname = m ? m[1] : `Relacao de Compras - AES ${new Date().toISOString().slice(0, 10)}.xlsx`;
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success('Relação de Compras gerada (já avaliados pelo Compras saem em amarelo).');
  } catch (e) {
    toast.error('Erro ao gerar a Relação de Compras.');
  }
}

// =================== PÁGINA PRINCIPAL ===================
// Uma tela por tipo (pedido da Adneia, 10/09/2026): ETR e Erro na Nota nao
// sao necessariamente cancelamento — cada fluxo tem a sua tela. O componente e
// o mesmo, parametrizado: triplicar o arquivo criaria tres copias pra divergir.
const CONFIG_TIPO = {
  aes: {
    titulo: 'Cancelamentos',
    subtitulo: 'Cancelamentos AES — pedido sem estoque no fornecedor',
    // Similares, indicadores mensais e tempo de finalizacao sao medidas do
    // fluxo AES; na tela dos outros tipos seriam numero de outro assunto.
    extras: true,
  },
  etr: {
    titulo: 'Falta na Produção (ETR)',
    subtitulo: 'Itens identificados na produção — perda, quebra ou falha de cadastro',
    extras: false,
  },
  erro_nota: {
    titulo: 'Erro na Nota',
    subtitulo: 'Notas rejeitadas — acionar o cliente e lançar nova entrega',
    extras: false,
  },
};

export default function Cancelamentos({ tipo = 'aes' }) {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState({ aes: {}, etr: {}, erro_nota: {} });
  const [similarCusto, setSimilarCusto] = useState(null); // dif média de custo original x similar
  const [refreshKey, setRefreshKey] = useState(0);
  // `tab` continua existindo porque o restante da tela (ciclo de vida, cards)
  // le por ele — mas agora vem da ROTA e nao muda mais dentro da tela.
  const tab = tipo;
  const cfg = CONFIG_TIPO[tipo] || CONFIG_TIPO.aes;
  const [showMensal, setShowMensal] = useState(false);
  const [mesesFiltro, setMesesFiltro] = useState(0);   // 0 = todo o período
  const [baseMes, setBaseMes] = useState('entrada');   // entrada | cancelamento
  const [showSimilares, setShowSimilares] = useState(false);
  const [showTempo, setShowTempo] = useState(false);
  const [tempoFinalizacao, setTempoFinalizacao] = useState(null);
  const [showGeral, setShowGeral] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/cancelamentos/stats`, { headers: getAuthHeader() });
      setStats(res.data || {});
    } catch (e) { /* ignore */ }
    try {
      const r2 = await axios.get(`${API_URL}/api/cancelamentos/similar-custo`, { headers: getAuthHeader() });
      setSimilarCusto(r2.data || null);
    } catch { setSimilarCusto(null); }
  }, [getAuthHeader]);

  const fetchTempoFinalizacao = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/cancelamentos/tempo-finalizacao`, { headers: getAuthHeader() });
      setTempoFinalizacao(r.data || null);
    } catch { setTempoFinalizacao(null); }
  }, [getAuthHeader]);

  useEffect(() => { fetchStats(); }, [fetchStats, refreshKey]);
  useEffect(() => { if (showTempo) fetchTempoFinalizacao(); }, [showTempo, fetchTempoFinalizacao, refreshKey]);

  const triggerRefresh = () => { setRefreshKey(k => k + 1); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            {tipo === 'aes' && <X className="h-6 w-6 text-red-600" />}
            {tipo === 'etr' && <Factory className="h-6 w-6 text-purple-600" />}
            {tipo === 'erro_nota' && <FileWarning className="h-6 w-6 text-red-600" />}
            {cfg.titulo}
          </h1>
          <p className="text-sm text-muted-foreground">{cfg.subtitulo}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={showGeral ? 'default' : 'outline'} size="sm" onClick={() => setShowGeral(v => !v)}>
            📋 Dados gerais
          </Button>
          {cfg.extras && (
            <>
              <Button variant={showSimilares ? 'default' : 'outline'} size="sm" onClick={() => setShowSimilares(v => !v)}>
                🔁 Similares
              </Button>
              <Button variant={showMensal ? 'default' : 'outline'} size="sm" onClick={() => setShowMensal(v => !v)}>
                <BarChart3 className="h-4 w-4 mr-1" /> Indicadores mensais
              </Button>
              <Button variant={showTempo ? 'default' : 'outline'} size="sm" onClick={() => setShowTempo(v => !v)}>
                <Clock className="h-4 w-4 mr-1" /> Tempo de Finalização
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={triggerRefresh}>
            <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
          </Button>
        </div>
      </div>

      {/* Card de stats do tipo desta tela (os outros tipos tem tela propria) */}
      <div className="grid grid-cols-1 gap-3">
        {tipo === 'aes' && (
        <div className="border rounded-lg p-4 bg-orange-50 dark:bg-orange-950/20 border-orange-200">
          <div className="flex items-center gap-2 mb-2">
            <ShoppingCart className="h-5 w-5 text-orange-600" />
            <h3 className="font-semibold text-orange-900 dark:text-orange-200">AES (Compras)</h3>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div><div className="text-2xl font-bold">{stats.aes?.total || 0}</div><div className="text-xs text-slate-500">Total</div></div>
            <div><div className="text-2xl font-bold text-amber-600">{stats.aes?.pendentes || 0}</div><div className="text-xs text-slate-500">Pendentes</div></div>
            <div><div className="text-2xl font-bold text-emerald-600">{stats.aes?.encerrados || 0}</div><div className="text-xs text-slate-500">Encerrados</div></div>
          </div>
        </div>
        )}

        {tipo === 'etr' && (
        <div className="border rounded-lg p-4 bg-purple-50 dark:bg-purple-950/20 border-purple-200">
          <div className="flex items-center gap-2 mb-2">
            <Factory className="h-5 w-5 text-purple-600" />
            <h3 className="font-semibold text-purple-900 dark:text-purple-200">ETR (Produção)</h3>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div><div className="text-2xl font-bold">{stats.etr?.total || 0}</div><div className="text-xs text-slate-500">Total</div></div>
            <div><div className="text-2xl font-bold text-amber-600">{stats.etr?.pendentes || 0}</div><div className="text-xs text-slate-500">Pendentes</div></div>
            <div><div className="text-2xl font-bold text-emerald-600">{stats.etr?.encerrados || 0}</div><div className="text-xs text-slate-500">Encerrados</div></div>
          </div>
        </div>
        )}

        {tipo === 'erro_nota' && (
        <div className="border rounded-lg p-4 bg-red-50 dark:bg-red-950/20 border-red-200">
          <div className="flex items-center gap-2 mb-2">
            <FileWarning className="h-5 w-5 text-red-600" />
            <h3 className="font-semibold text-red-900 dark:text-red-200">Erro na Nota</h3>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div><div className="text-2xl font-bold">{stats.erro_nota?.total || 0}</div><div className="text-xs text-slate-500">Total</div></div>
            <div><div className="text-2xl font-bold text-amber-600">{stats.erro_nota?.pendentes || 0}</div><div className="text-xs text-slate-500">Pendentes</div></div>
            <div><div className="text-2xl font-bold text-emerald-600">{stats.erro_nota?.encerrados || 0}</div><div className="text-xs text-slate-500">Encerrados</div></div>
          </div>
        </div>
        )}
      </div>

      {/* Ciclo de vida do tipo ativo (qtd + valor produto+frete) — atrás do botão "Dados gerais" */}
      {showGeral && stats.lifecycle?.[tab] && (() => {
        const lc = stats.lifecycle[tab];
        const tipoLabel = tab === 'aes' ? 'AES (Compras)' : tab === 'etr' ? 'ETR (Produção)' : 'Erro na Nota';
        const cards = [
          { k: 'pendente', label: 'Pendentes', sub: 'sem ticket', cls: 'border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800', txt: 'text-slate-700 dark:text-slate-200', lbl: 'text-slate-600 dark:text-slate-300' },
          { k: 'em_tratativa', label: 'Em tratativa', sub: 'com ticket', cls: 'border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/20', txt: 'text-amber-800 dark:text-amber-200', lbl: 'text-amber-700 dark:text-amber-300' },
          { k: 'similar', label: 'Similar', sub: 'recuperado', cls: 'border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/20', txt: 'text-blue-800 dark:text-blue-200', lbl: 'text-blue-700 dark:text-blue-300' },
          { k: 'cancelado', label: 'Cancelado', sub: 'entrega cancelada', cls: 'border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20', txt: 'text-red-800 dark:text-red-200', lbl: 'text-red-700 dark:text-red-300' },
          { k: 'entregue', label: 'Entregue', sub: 'faturado mesmo assim', cls: 'border-teal-200 dark:border-teal-900 bg-teal-50 dark:bg-teal-950/20', txt: 'text-teal-800 dark:text-teal-200', lbl: 'text-teal-700 dark:text-teal-300' },
          { k: 'encerrado', label: 'Encerrado', sub: 'todos desfechos', cls: 'border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/20', txt: 'text-emerald-800 dark:text-emerald-200', lbl: 'text-emerald-700 dark:text-emerald-300' },
        ];
        const desdeFmt = stats.lifecycle?.desde
          ? `${stats.lifecycle.desde.slice(5, 7)}/${stats.lifecycle.desde.slice(0, 4)}`
          : null;
        return (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
              Ciclo de vida — {tipoLabel}
              {desdeFmt && <span className="font-normal normal-case"> · acumulado desde {desdeFmt}</span>}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
              {cards.map(c => (
                <div key={c.k} className={`rounded-lg border px-3 py-2 ${c.cls}`}>
                  <div className={`text-[11px] font-semibold uppercase tracking-wide ${c.lbl}`}>{c.label}<span className="font-normal normal-case opacity-70"> · {c.sub}</span></div>
                  <div className="flex items-baseline gap-2">
                    <span className={`text-2xl font-bold ${c.txt}`}>{lc[c.k]?.n || 0}</span>
                    <span className={`text-sm font-medium ${c.lbl}`}>{formatMoney(lc[c.k]?.valor || 0)}</span>
                  </div>
                  {c.k === 'similar' && tab === 'aes' && similarCusto?.n > 0 && (
                    <div className={`text-[10px] mt-0.5 ${c.lbl}`}
                      title={`Média sobre ${similarCusto.n} caso(s) com similar enviado · custo médio: original ${formatMoney(similarCusto.custo_orig_medio)} → similar ${formatMoney(similarCusto.custo_sim_medio)}`}>
                      Δ custo méd.: <b>{similarCusto.dif_media >= 0 ? '+' : '−'}{formatMoney(Math.abs(similarCusto.dif_media))}</b>
                      {similarCusto.dif_media > 0 ? ' (mais caro)' : similarCusto.dif_media < 0 ? ' (economia)' : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Painel: similares enviados — original × similar (custos + diferença) */}
      {showSimilares && (
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h3 className="font-semibold">🔁 Similares enviados — original × similar (custo)</h3>
            <span className="text-xs text-muted-foreground">
              {similarCusto?.total_pares || 0} itens · dif. média {similarCusto?.n ? `${similarCusto.dif_media >= 0 ? '+' : '−'}${formatMoney(Math.abs(similarCusto.dif_media))}` : '—'} ({similarCusto?.n || 0} com custo)
            </span>
          </div>
          {/* Quanto já gastamos a mais, a CUSTO (Adneia, 17/09). Respeita o
              mesmo recorte de período do gráfico: o acumulado desde 09/2025
              inclui o tempo em que o processo ainda estava sendo ajustado. */}
          {!!similarCusto?.n && (() => {
            const corte = (() => {
              if (!mesesFiltro) return '';
              const d = new Date();
              d.setMonth(d.getMonth() - (mesesFiltro - 1));
              return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            })();
            const visiveis = (similarCusto.itens || []).filter(i =>
              i.diferenca !== null && i.diferenca !== undefined &&
              (!corte || String(i.data || '').slice(0, 7) >= corte));
            const soma = (f) => visiveis.filter(f).reduce((a, i) => a + i.diferenca, 0);
            const tot = soma(() => true);
            const acima = soma(i => i.diferenca > 0);
            const abaixo = soma(i => i.diferenca < 0);
            const nAcima = visiveis.filter(i => i.diferenca > 0).length;
            const nAbaixo = visiveis.filter(i => i.diferenca < 0).length;
            return (
              <div className="flex flex-wrap items-center gap-4 text-xs mb-3 px-1">
                <span className="text-muted-foreground">
                  {mesesFiltro ? `Últimos ${mesesFiltro} meses` : 'Todo o período'} ·{' '}
                  <strong>{visiveis.length}</strong> com custo
                </span>
                <span className="text-muted-foreground">
                  Gasto a mais no total:{' '}
                  <strong className={tot > 0 ? 'text-red-600' : 'text-emerald-700'}>
                    {tot >= 0 ? '+' : '−'}{formatMoney(Math.abs(tot))}
                  </strong>
                  <span className="text-slate-400"> (líquido)</span>
                </span>
                <span className="text-muted-foreground">
                  Só os mais caros: <strong className="text-red-600">+{formatMoney(acima)}</strong>
                  <span className="text-slate-400"> em {nAcima} {nAcima === 1 ? 'item' : 'itens'}</span>
                </span>
                {!!nAbaixo && (
                  <span className="text-muted-foreground">
                    Economizados: <strong className="text-emerald-700">−{formatMoney(Math.abs(abaixo))}</strong>
                    <span className="text-slate-400"> em {nAbaixo} {nAbaixo === 1 ? 'item' : 'itens'}</span>
                  </span>
                )}
              </div>
            );
          })()}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-y">
                <tr className="text-left">
                  <th className="px-2 py-2 font-semibold">Entrega</th>
                  <th className="px-2 py-2 font-semibold">Produto</th>
                  <th className="px-2 py-2 font-semibold">SKU original</th>
                  <th className="px-2 py-2 font-semibold">SKU similar</th>
                  <th className="px-2 py-2 font-semibold text-right">Preço venda</th>
                  <th className="px-2 py-2 font-semibold text-right">Custo original</th>
                  <th className="px-2 py-2 font-semibold text-right">Custo similar</th>
                  <th className="px-2 py-2 font-semibold text-right">Diferença</th>
                </tr>
              </thead>
              <tbody>
                {(similarCusto?.itens || []).map((it, idx) => (
                  <tr key={idx} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="px-2 py-1.5 font-mono whitespace-nowrap">{it.entrega}</td>
                    <td className="px-2 py-1.5 max-w-[280px] truncate" title={it.produto}>{it.produto || '—'}</td>
                    <td className="px-2 py-1.5 font-mono whitespace-nowrap">{it.sku_original}</td>
                    <td className="px-2 py-1.5 font-mono whitespace-nowrap" title={it.sku_registrado ? `Despachado: ${it.sku_similar} · registrado no chamado: ${it.sku_registrado}` : it.nome_similar}>
                      {it.sku_similar}
                      {it.sku_registrado ? <span className="ml-1 text-[10px] text-amber-600">≠ reg</span> : null}
                    </td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">{it.preco_venda != null ? formatMoney(it.preco_venda) : '—'}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">{it.custo_original != null ? formatMoney(it.custo_original) : '—'}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">{it.custo_similar != null ? formatMoney(it.custo_similar) : '—'}</td>
                    <td className={`px-2 py-1.5 text-right font-semibold whitespace-nowrap ${it.diferenca == null ? 'text-muted-foreground' : it.diferenca > 0 ? 'text-red-600' : it.diferenca < 0 ? 'text-emerald-600' : ''}`}>
                      {it.diferenca == null ? '—' : `${it.diferenca >= 0 ? '+' : '−'}${formatMoney(Math.abs(it.diferenca))}`}
                    </td>
                  </tr>
                ))}
                {(!similarCusto?.itens || similarCusto.itens.length === 0) && (
                  <tr><td colSpan={8} className="px-2 py-6 text-center text-muted-foreground">Nenhum similar enviado.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            Custo = último preço de compra. Diferença = custo do similar − custo do original (vermelho = mais caro, verde = economia). Itens sem custo cadastrado aparecem com "—".
          </p>
        </div>
      )}

      {/* Indicadores mensais: solicitações (barras) x desfechos (linhas de tendência) */}
      {showMensal && stats.lifecycle?.mensal && (() => {
        // entrada = mês em que fomos acionados | cancelamento = mês do desfecho
        const serie = (baseMes === 'cancelamento' && stats.lifecycle.mensal_saida)
          ? stats.lifecycle.mensal_saida
          : stats.lifecycle.mensal;
        // Período: corta os últimos N meses da série (Adneia, 17/09 — "queria
        // saber nos últimos 2 meses como ficou o cancelamento em valores").
        const dados = (mesesFiltro > 0 ? serie.slice(-mesesFiltro) : serie).map(r => ({
          ...r,
          mesLabel: `${r.mes.slice(5, 7)}/${r.mes.slice(2, 4)}`,
          taxaCancel: r.solicitacoes ? (r.cancelado / r.solicitacoes * 100) : 0,
        }));
        const soma = (campo) => dados.reduce((a, r) => a + (r[campo] || 0), 0);
        const totPeriodo = {
          solicitacoes: soma('solicitacoes'), cancelado: soma('cancelado'),
          similar: soma('similar'), entregue: soma('entregue'),
          v_solicitacoes: soma('v_solicitacoes'), v_cancelado: soma('v_cancelado'),
          v_similar: soma('v_similar'), v_entregue: soma('v_entregue'),
        };
        return (
          <div className="border rounded-lg p-4 bg-card">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <BarChart3 className="h-5 w-5 text-indigo-600" />
              <h3 className="font-semibold">Indicadores mensais — Solicitações e desfechos</h3>
              <select
                value={baseMes}
                onChange={e => setBaseMes(e.target.value)}
                className="ml-auto text-xs border rounded px-2 py-1 bg-background"
                title="Entrada = mês em que fomos acionados · Cancelamento = mês em que o caso foi encerrado"
              >
                <option value="entrada">por mês de entrada</option>
                <option value="cancelamento">por mês do cancelamento</option>
              </select>
              <select
                value={mesesFiltro}
                onChange={e => setMesesFiltro(Number(e.target.value))}
                className="text-xs border rounded px-2 py-1 bg-background"
                title="Recorta os últimos meses da série"
              >
                <option value={0}>todo o período</option>
                <option value={2}>últimos 2 meses</option>
                <option value={3}>últimos 3 meses</option>
                <option value={6}>últimos 6 meses</option>
                <option value={12}>últimos 12 meses</option>
              </select>
            </div>
            {/* Totais do recorte — é o que responde "quanto foi em valores" */}
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs mb-3">
              <span className="text-muted-foreground">
                {baseMes === 'cancelamento' ? 'Encerrados' : 'Solicitações'}:{' '}
                <strong>{totPeriodo.solicitacoes}</strong> · {formatMoney(totPeriodo.v_solicitacoes)}
              </span>
              <span className="text-red-600">
                Cancelados: <strong>{totPeriodo.cancelado}</strong> · {formatMoney(totPeriodo.v_cancelado)}
              </span>
              <span className="text-blue-600">
                Similares: <strong>{totPeriodo.similar}</strong> · {formatMoney(totPeriodo.v_similar)}
              </span>
              <span className="text-emerald-700">
                Entregues: <strong>{totPeriodo.entregue}</strong> · {formatMoney(totPeriodo.v_entregue)}
              </span>
              <span className="text-muted-foreground">
                % cancelamento:{' '}
                <strong>{totPeriodo.solicitacoes ? (totPeriodo.cancelado / totPeriodo.solicitacoes * 100).toFixed(1) : '0,0'}%</strong>
              </span>
            </div>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <ComposedChart data={dados} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="mesLabel" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip formatter={(value, name, item) => {
                    const sol = item?.payload?.solicitacoes || 0;
                    if (name === 'Solicitações' || !sol) return value;
                    return `${value} (${(value / sol * 100).toFixed(1)}%)`;
                  }} />
                  <Legend />
                  <Bar dataKey="solicitacoes" name="Solicitações" fill="#cbd5e1" radius={[3, 3, 0, 0]} />
                  <Line type="monotone" dataKey="cancelado" name="Cancelados" stroke="#dc2626" strokeWidth={2} dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="similar" name="Similares" stroke="#2563eb" strokeWidth={2} dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="entregue" name="Entregues" stroke="#0d9488" strokeWidth={2} dot={{ r: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              Barras = volume de <strong>solicitações</strong> abertas no mês. Linhas de tendência = desfechos
              (Cancelado = entrega cancelada · Similar = recuperado · Entregue = faturado no mesmo código apesar da solicitação).
              Mês pela data de abertura do cancelamento.
            </div>

            {/* Gráfico 2: valores em R$ + % de cancelamento */}
            <h3 className="font-semibold text-sm mt-5 mb-2">Valores (R$) e % de cancelamento</h3>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <ComposedChart data={dados} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="mesLabel" fontSize={12} />
                  <YAxis yAxisId="esq" fontSize={12} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                  <YAxis yAxisId="dir" orientation="right" fontSize={12} domain={[0, 'auto']} tickFormatter={(v) => `${Math.round(v)}%`} />
                  <Tooltip formatter={(value, name) => (name === '% Cancel.' ? `${Number(value).toFixed(1)}%` : formatMoney(value))} />
                  <Legend />
                  <Bar yAxisId="esq" dataKey="v_solicitacoes" name="Solicitações (R$)" fill="#cbd5e1" radius={[3, 3, 0, 0]} />
                  <Line yAxisId="esq" type="monotone" dataKey="v_cancelado" name="Cancelados (R$)" stroke="#dc2626" strokeWidth={2} dot={{ r: 2 }} />
                  <Line yAxisId="esq" type="monotone" dataKey="v_similar" name="Similares (R$)" stroke="#2563eb" strokeWidth={2} dot={{ r: 2 }} />
                  <Line yAxisId="esq" type="monotone" dataKey="v_entregue" name="Entregues (R$)" stroke="#0d9488" strokeWidth={2} dot={{ r: 2 }} />
                  <Line yAxisId="dir" type="monotone" dataKey="taxaCancel" name="% Cancel." stroke="#b45309" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              Valores em R$ (produto + frete) no eixo esquerdo. A linha tracejada laranja é a <strong>% de cancelamento</strong>
              (cancelados ÷ solicitações) no eixo direito.
            </div>
          </div>
        );
      })()}

      {/* Tempo de Finalização: (1) aging do backlog atual · (2) SLA 15d prospectivo desde hoje */}
      {/* O card e todo montado com dados do AES; no ETR ele nao se aplica (Adnéia, 22/09). */}
      {showTempo && tempoFinalizacao && tipo === 'aes' && (() => {
        const tf = tempoFinalizacao;
        // Por ora só AES (Compras) — ETR e Erro na Nota ainda são controlados em planilha (Excel).
        // Quando entrarem aqui, é só voltar a listar os 3 tipos nos loops abaixo.
        const tipoLabel = { aes: 'AES (Compras)', etr: 'ETR (Produção)', erro_nota: 'Erro na Nota' };
        const faixaOrder = ['0-5', '6-10', '11-14', '15-20', '21+'];
        const dadosFaixas = faixaOrder.map(f => ({
          faixa: f,
          aes: tf.base_atual?.aes?.faixas?.[f] || 0,
        }));
        const novosAes = (tf.novos_itens || []).filter(it => it.tipo === 'aes');
        const totalNovosAes = novosAes.length;
        const novosDentro = novosAes.filter(it => it.dentro_prazo).length;
        const novosFora = totalNovosAes - novosDentro;
        const novosDentroEncerrados = novosAes.filter(it => it.dentro_prazo && it.status === 'encerrado').length;
        const novosDentroAbertos = novosDentro - novosDentroEncerrados;
        const novosForaEncerrados = novosAes.filter(it => !it.dentro_prazo && it.status === 'encerrado').length;
        const novosForaAbertos = novosFora - novosForaEncerrados;
        const pctDentro = totalNovosAes ? Math.round((novosDentro / totalNovosAes) * 100) : null;

        return (
          <div className="space-y-4">
            {/* Indicador 1 — base atual (aging) */}
            <div className="border rounded-lg p-4 bg-card">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="h-5 w-5 text-indigo-600" />
                <h3 className="font-semibold">Tempo de Finalização — Base atual</h3>
              </div>
              <p className="text-[11px] text-muted-foreground mb-3">
                Dias entre o acionamento (data de abertura) e o encerramento — para os ainda pendentes, conta até hoje ({formatData(tf.hoje)}).
                SLA definido em reunião: <strong>{tf.sla_dias} dias corridos</strong>.
              </p>
              {/* META (Adnéia, 22/09): nenhum atendimento PENDENTE pode passar de 14 dias. */}
              {(() => {
                const pf = tf.base_atual?.aes?.pendentes_fora;
                if (pf == null) return null;
                const ok = pf === 0;
                return (
                  <div className="mb-3 rounded-lg border border-orange-200 dark:border-orange-900 bg-orange-50/70 dark:bg-orange-950/20 px-3 py-2">
                    <div className="text-[11px] font-bold text-orange-700 dark:text-orange-300">
                      META · Cancelamento AES — finalização da tratativa em até {tf.sla_dias} dias
                    </div>
                    <div className={`text-sm font-semibold mt-0.5 ${ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                      {ok ? '✅ meta atingida — nenhum pendente fora do prazo'
                          : `⚠️ ${pf} pendente(s) com mais de ${tf.sla_dias} dias`}
                    </div>
                  </div>
                );
              })()}
              <div className="grid grid-cols-1 gap-3 mb-3">
                {(() => {
                  const b = tf.base_atual?.aes || {};
                  const antesDias = b.mais_antigo_antes_dias;
                  const temAntes = typeof antesDias === 'number' && antesDias >= 0;
                  return (
                    <div className="rounded-lg border px-3 py-2.5 bg-slate-50 dark:bg-slate-900/30 flex items-start justify-between gap-4">
                      <div>
                        <div className="text-[11px] font-semibold uppercase text-slate-600 dark:text-slate-300">{tipoLabel.aes}</div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-bold">{b.media_dias ?? 0}d</span>
                          <span className="text-xs text-muted-foreground">média · {b.n || 0} casos</span>
                        </div>
                        <div className="text-[11px] mt-1">
                          <span className="text-emerald-600 font-semibold">{b.dentro_prazo || 0} dentro do prazo</span>
                          {' · '}
                          <span className="text-red-600 font-semibold">{b.fora_prazo || 0} fora do prazo</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Pedido mais antigo</div>
                        <div className="text-2xl font-bold leading-tight text-amber-600">
                          {temAntes ? `${antesDias}d` : '—'}
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {temAntes
                            ? <>antes de 20/08 · backlog{b.mais_antigo_antes_pedido ? ` · ${b.mais_antigo_antes_pedido}` : ''}</>
                            : 'nenhum pendente antes de 20/08'}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <ComposedChart data={dadosFaixas} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="faixa" fontSize={12} />
                    <YAxis fontSize={12} allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="aes" name="AES (Compras)" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                Eixo = faixas de dias corridos desde o acionamento. Mediana AES: <strong>{tf.base_atual?.aes?.mediana_dias ?? 0}d</strong>.
                A faixa 11-14 ainda está dentro do prazo; 15+ já está fora do SLA de {tf.sla_dias} dias (prazo do ELO após chegar o acionamento — Compras tem, antes disso, até 7 dias pra acionar).
              </div>
            </div>

            {/* Indicador 2 — SLA prospectivo a partir de hoje */}
            <div className="border rounded-lg p-4 bg-card">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="h-5 w-5 text-teal-600" />
                <h3 className="font-semibold">SLA {tf.sla_dias} dias — Acionamentos desde 20/08</h3>
              </div>
              <p className="text-[11px] text-muted-foreground mb-3">
                Considera só os acionamentos abertos a partir de {formatData(tf.sla_inicio)} — acompanha se os que estão entrando
                seguem o prazo de {tf.sla_dias} dias corridos definido em reunião.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="rounded-lg border px-3 py-2 bg-slate-50 dark:bg-slate-900/30">
                  <div className="text-[11px] font-semibold uppercase text-slate-600 dark:text-slate-300">Total desde 20/08</div>
                  <div className="text-2xl font-bold">{totalNovosAes}</div>
                </div>
                <div className="rounded-lg border px-3 py-2 bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200">
                  <div className="text-[11px] font-semibold uppercase text-emerald-700">Dentro do prazo</div>
                  <div className="text-2xl font-bold text-emerald-700">{novosDentro}</div>
                  <div className="text-[10px] text-emerald-700">{novosDentroEncerrados} encerrados · {novosDentroAbertos} em aberto</div>
                </div>
                <div className="rounded-lg border px-3 py-2 bg-red-50 dark:bg-red-950/20 border-red-200">
                  <div className="text-[11px] font-semibold uppercase text-red-700">Fora do prazo</div>
                  <div className="text-2xl font-bold text-red-700">{novosFora}</div>
                  <div className="text-[10px] text-red-700">{novosForaEncerrados} encerrados · {novosForaAbertos} em aberto</div>
                </div>
                <div className="rounded-lg border px-3 py-2 bg-indigo-50 dark:bg-indigo-950/20 border-indigo-200">
                  <div className="text-[11px] font-semibold uppercase text-indigo-700">Aderência ao SLA</div>
                  <div className="text-2xl font-bold text-indigo-700">{pctDentro != null ? `${pctDentro}%` : '—'}</div>
                </div>
                {(() => {
                  const md = tf.base_atual?.aes?.mais_antigo_desde_dias;
                  const tem = typeof md === 'number' && md >= 0;
                  const est = tem && md > (tf.sla_dias || 14);
                  return (
                    <div className={`rounded-lg border px-3 py-2 ${est ? 'bg-red-50 dark:bg-red-950/20 border-red-200' : 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200'}`}>
                      <div className={`text-[11px] font-semibold uppercase ${est ? 'text-red-700' : 'text-emerald-700'}`}>Mais antigo desde 20/08</div>
                      <div className={`text-2xl font-bold ${!tem ? 'text-muted-foreground' : est ? 'text-red-700' : 'text-emerald-700'}`}>{tem ? `${md}d` : '—'}</div>
                      <div className={`text-[10px] ${est ? 'text-red-700' : 'text-emerald-700'}`}>{tem ? (est ? `⚠️ passou de ${tf.sla_dias || 14}d` : `✓ dentro de ${tf.sla_dias || 14}d`) : 'sem pendentes'}{tem && tf.base_atual?.aes?.mais_antigo_desde_pedido ? ` · ${tf.base_atual.aes.mais_antigo_desde_pedido}` : ''}</div>
                    </div>
                  );
                })()}
              </div>
              {novosAes.length > 0 && (
                <div className="mt-3 overflow-x-auto max-h-[220px] overflow-y-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 border-b sticky top-0">
                      <tr className="text-left">
                        <th className="px-2 py-1.5 font-semibold">Entrega</th>
                        <th className="px-2 py-1.5 font-semibold">Tipo</th>
                        <th className="px-2 py-1.5 font-semibold">Acionado em</th>
                        <th className="px-2 py-1.5 font-semibold">Status</th>
                        <th className="px-2 py-1.5 font-semibold text-right">Dias</th>
                      </tr>
                    </thead>
                    <tbody>
                      {novosAes.map((it, i) => (
                        <tr key={i} className={`border-b last:border-0 ${it.dentro_prazo ? '' : 'bg-red-50 dark:bg-red-950/20'}`}>
                          <td className="px-2 py-1 font-mono whitespace-nowrap">{it.numero_pedido}</td>
                          <td className="px-2 py-1 whitespace-nowrap">{tipoLabel[it.tipo]}</td>
                          <td className="px-2 py-1 whitespace-nowrap">{formatData(it.data_criacao)}</td>
                          <td className="px-2 py-1 whitespace-nowrap">{it.status === 'encerrado' ? 'Encerrado' : 'Em aberto'}</td>
                          <td className={`px-2 py-1 text-right font-semibold whitespace-nowrap ${it.dentro_prazo ? 'text-emerald-600' : 'text-red-600'}`}>{it.dias}d</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {novosAes.length === 0 && (
                <p className="text-xs text-muted-foreground mt-3">Nenhum acionamento desde 20/08.</p>
              )}
            </div>
          </div>
        );
      })()}

      {/* Uma tela por tipo: a tabela do tipo da rota, sem abas */}
      <div className="mt-4">
        <TabelaCancelamentos tipo={tipo} refreshKey={refreshKey} onRefresh={triggerRefresh} />
      </div>
    </div>
  );
}
